import * as THREE from 'three';
import './style.css';
import { AFFINITIES, CHARACTERS, CLANS, ENEMY_ARCHETYPES, createActions } from './game/content.js';
import { calculateDamage, elementMultiplier, masteryNeeded, resolveProgress, xpNeeded } from './game/rules.js';
import { loadProgress, migrateSave, saveProgressData, SAVE_KEY } from './game/save.js';
import { attachRiggedModel, playRiggedAnimation, updateRiggedModel } from './game/rigged-model.js';

const $ = (selector) => document.querySelector(selector);
const $$ = (selector) => [...document.querySelectorAll(selector)];

const canvas = $('#sceneCanvas');
const battlefield = $('#battlefield');
const scene = new THREE.Scene();
const camera = new THREE.PerspectiveCamera(34, 1, 0.1, 100);
const renderer = new THREE.WebGLRenderer({ canvas, alpha: true, antialias: true, powerPreference: 'high-performance' });
renderer.setPixelRatio(Math.min(window.devicePixelRatio, 1.75));
renderer.shadowMap.enabled = true;
renderer.shadowMap.type = THREE.PCFShadowMap;
renderer.outputColorSpace = THREE.SRGBColorSpace;
renderer.toneMapping = THREE.ACESFilmicToneMapping;
renderer.toneMappingExposure = 1.05;

const world = new THREE.Group();
scene.add(world);

scene.add(new THREE.HemisphereLight(0x8ab8d8, 0x10131c, 2.25));
const moonLight = new THREE.DirectionalLight(0x9bc7ff, 3.2);
moonLight.position.set(-4, 8, 6);
moonLight.castShadow = true;
moonLight.shadow.mapSize.set(1024, 1024);
moonLight.shadow.camera.left = -8;
moonLight.shadow.camera.right = 8;
moonLight.shadow.camera.top = 7;
moonLight.shadow.camera.bottom = -4;
scene.add(moonLight);

const rimLight = new THREE.DirectionalLight(0x2bc9e5, 2.0);
rimLight.position.set(5, 3, -4);
scene.add(rimLight);

const floor = new THREE.Mesh(
  new THREE.CircleGeometry(6.7, 64),
  new THREE.MeshStandardMaterial({ color: 0x15202b, transparent: true, opacity: 0.58, roughness: 1, metalness: 0 })
);
floor.rotation.x = -Math.PI / 2;
floor.scale.y = 0.44;
floor.position.set(0, -0.04, .25);
floor.receiveShadow = true;
world.add(floor);

const ring = new THREE.Mesh(
  new THREE.RingGeometry(3.9, 3.96, 64),
  new THREE.MeshBasicMaterial({ color: 0x58d6e6, transparent: true, opacity: .16, side: THREE.DoubleSide })
);
ring.rotation.x = -Math.PI / 2;
ring.scale.y = .44;
ring.position.y = -.025;
world.add(ring);

const toon = (color, extra = {}) => new THREE.MeshStandardMaterial({
  color,
  roughness: .72,
  metalness: .04,
  flatShading: true,
  ...extra,
});

function mesh(geometry, material, parent, position, rotation = [0, 0, 0], scale = [1, 1, 1]) {
  const object = new THREE.Mesh(geometry, material);
  object.position.set(...position);
  object.rotation.set(...rotation);
  object.scale.set(...scale);
  object.castShadow = true;
  object.receiveShadow = true;
  parent.add(object);
  return object;
}

function addLantern(x, z, height) {
  const lantern = new THREE.Group();
  lantern.position.set(x, 0, z);
  const wood = toon(0x1b1413);
  mesh(new THREE.CylinderGeometry(.035, .05, height, 6), wood, lantern, [0, height / 2, 0]);
  mesh(new THREE.BoxGeometry(.36, .07, .36), wood, lantern, [0, height, 0]);
  mesh(new THREE.BoxGeometry(.3, .3, .3), toon(0xe86d24, { emissive: 0xa63d12, emissiveIntensity: 2 }), lantern, [0, height + .17, 0]);
  mesh(new THREE.ConeGeometry(.29, .18, 4), wood, lantern, [0, height + .43, 0], [0, Math.PI / 4, 0]);
  const light = new THREE.PointLight(0xff8c38, 7, 4.8, 1.7);
  light.position.set(0, height + .25, .1);
  lantern.add(light);
  world.add(lantern);
  return lantern;
}

[
  [-4.7, -1.9, 1.05],
  [-1.55, -2.55, 1.2],
  [1.55, -2.55, 1.2],
  [4.7, -1.9, 1.05],
].forEach((args) => addLantern(...args));

function createNinja({ side, primary, secondary, accent, skin, hair, enemy = false }) {
  const root = new THREE.Group();
  const model = new THREE.Group();
  root.add(model);
  root.userData.model = model;
  root.userData.baseX = side * 2.05;
  root.position.set(root.userData.baseX, 0, 0);
  root.rotation.y = side < 0 ? .08 : -.08;

  const primaryMat = toon(primary);
  const secondaryMat = toon(secondary);
  const accentMat = toon(accent, { emissive: accent, emissiveIntensity: .08 });
  const skinMat = toon(skin);
  const hairMat = toon(hair);
  const darkMat = toon(0x101722);
  const metalMat = toon(0x8b9eae, { metalness: .62, roughness: .35 });
  const eyeWhiteMat = toon(0xe9edf0, { roughness: .5 });
  const wrapMat = toon(0xb8c0c5);
  const leatherMat = toon(0x352523);

  // Layered torso with a clear shoulder, chest, waist and hip silhouette.
  mesh(new THREE.CylinderGeometry(.31, .4, .76, 8), primaryMat, model, [0, 1.38, 0], [0, 0, side * -.035], [1, 1, .72]);
  mesh(new THREE.CapsuleGeometry(.34, .18, 4, 8), secondaryMat, model, [0, 1.66, -.015], [0, 0, side * -.035], [1.18, .72, .72]);
  mesh(new THREE.BoxGeometry(.62, .34, .105), secondaryMat, model, [0, 1.46, .285], [0, 0, side * -.035]);
  mesh(new THREE.BoxGeometry(.06, .31, .025), accentMat, model, [0, 1.46, .345], [0, 0, side * -.035]);
  mesh(new THREE.BoxGeometry(.72, .12, .48), accentMat, model, [0, 1.08, .01], [0, 0, side * -.035]);
  mesh(new THREE.CylinderGeometry(.28, .31, .2, 8), secondaryMat, model, [0, .96, 0], [0, 0, side * -.035], [1, 1, .72]);
  mesh(new THREE.BoxGeometry(.26, .35, .08), primaryMat, model, [-.19, .86, .08], [0, 0, -.12]);
  mesh(new THREE.BoxGeometry(.26, .35, .08), primaryMat, model, [.19, .86, .08], [0, 0, .12]);
  mesh(new THREE.BoxGeometry(.2, .29, .12), leatherMat, model, [side * -.39, 1.02, -.06], [0, side * .2, 0]);
  mesh(new THREE.CylinderGeometry(.095, .105, .15, 8), skinMat, model, [0, 1.84, 0]);

  const head = new THREE.Group();
  head.position.set(side * .015, 2.16, .02);
  model.add(head);
  mesh(new THREE.SphereGeometry(.335, 20, 16), skinMat, head, [0, 0, 0], [0, 0, 0], [1, 1.08, .92]);
  mesh(new THREE.SphereGeometry(.07, 10, 8), skinMat, head, [-.34, 0, -.01], [0, 0, 0], [.55, 1, .65]);
  mesh(new THREE.SphereGeometry(.07, 10, 8), skinMat, head, [.34, 0, -.01], [0, 0, 0], [.55, 1, .65]);
  mesh(new THREE.SphereGeometry(.35, 16, 12), hairMat, head, [0, .18, -.025], [0, 0, 0], [1.02, .68, .95]);
  mesh(new THREE.BoxGeometry(.72, .14, .48), darkMat, head, [0, .14, .02]);
  mesh(new THREE.BoxGeometry(.4, .15, .035), metalMat, head, [0, .145, .285]);
  mesh(new THREE.BoxGeometry(.025, .11, .018), accentMat, head, [0, .145, .307], [0, 0, .65]);
  mesh(new THREE.BoxGeometry(.025, .11, .018), accentMat, head, [0, .145, .307], [0, 0, -.65]);
  for (let i = 0; i < 10; i++) {
    const angle = (i / 9) * Math.PI - Math.PI / 2;
    const length = .36 + (i % 3) * .075;
    mesh(new THREE.ConeGeometry(.105, length, 5), hairMat, head,
      [Math.sin(angle) * .28, .29 + Math.cos(angle) * .075, -.035 - Math.abs(i - 4.5) * .008],
      [0, Math.sin(angle) * .12, -angle * .5 + (i - 4.5) * .105]);
  }
  // Eyes, pupils, brows, nose and mouth remain readable from the game camera.
  [-.12, .12].forEach((x) => {
    mesh(new THREE.SphereGeometry(.052, 10, 8), eyeWhiteMat, head, [x, .015, .304], [0, 0, 0], [1.25, .48, .28]);
    mesh(new THREE.SphereGeometry(.023, 8, 6), darkMat, head, [x + side * .004, .015, .329], [0, 0, 0], [.7, 1, .35]);
    mesh(new THREE.BoxGeometry(.12, .018, .018), hairMat, head, [x, .085, .318], [0, 0, x < 0 ? -.08 : .08]);
  });
  mesh(new THREE.ConeGeometry(.026, .09, 5), skinMat, head, [0, -.04, .344], [Math.PI / 2, 0, 0]);
  mesh(new THREE.BoxGeometry(.105, .014, .012), darkMat, head, [0, -.145, .319]);
  mesh(new THREE.BoxGeometry(.08, .012, .014), accentMat, head, [-.2, -.075, .315], [0, 0, .08]);
  mesh(new THREE.BoxGeometry(.08, .012, .014), accentMat, head, [.2, -.075, .315], [0, 0, -.08]);

  const leftArm = new THREE.Group();
  leftArm.position.set(-.47, 1.62, 0);
  model.add(leftArm);
  mesh(new THREE.SphereGeometry(.17, 10, 8), primaryMat, leftArm, [0, -.04, 0], [0, 0, 0], [1.05, .8, .82]);
  mesh(new THREE.CapsuleGeometry(.105, .25, 4, 8), secondaryMat, leftArm, [-.035, -.22, 0], [0, 0, -.13]);
  mesh(new THREE.SphereGeometry(.105, 10, 8), metalMat, leftArm, [-.07, -.42, 0]);
  mesh(new THREE.CapsuleGeometry(.095, .24, 4, 8), primaryMat, leftArm, [-.08, -.55, .015], [0, 0, .03]);
  mesh(new THREE.CylinderGeometry(.11, .11, .13, 8), wrapMat, leftArm, [-.08, -.7, .015]);
  mesh(new THREE.SphereGeometry(.115, 10, 8), skinMat, leftArm, [-.08, -.81, .035], [0, 0, 0], [.9, 1.05, .72]);

  const rightArm = new THREE.Group();
  rightArm.position.set(.47, 1.62, 0);
  model.add(rightArm);
  mesh(new THREE.SphereGeometry(.17, 10, 8), primaryMat, rightArm, [0, -.04, 0], [0, 0, 0], [1.05, .8, .82]);
  mesh(new THREE.CapsuleGeometry(.105, .25, 4, 8), secondaryMat, rightArm, [.035, -.22, 0], [0, 0, .13]);
  mesh(new THREE.SphereGeometry(.105, 10, 8), metalMat, rightArm, [.07, -.42, 0]);
  mesh(new THREE.CapsuleGeometry(.095, .24, 4, 8), primaryMat, rightArm, [.08, -.55, .015], [0, 0, -.03]);
  mesh(new THREE.CylinderGeometry(.11, .11, .13, 8), wrapMat, rightArm, [.08, -.7, .015]);
  mesh(new THREE.SphereGeometry(.115, 10, 8), skinMat, rightArm, [.08, -.81, .035], [0, 0, 0], [.9, 1.05, .72]);

  const leftLeg = new THREE.Group();
  leftLeg.position.set(-.2, .9, 0);
  model.add(leftLeg);
  mesh(new THREE.CapsuleGeometry(.14, .28, 4, 8), secondaryMat, leftLeg, [0, -.18, 0], [0, 0, -.035]);
  mesh(new THREE.SphereGeometry(.135, 10, 8), primaryMat, leftLeg, [-.015, -.43, .015], [0, 0, 0], [1, .8, .9]);
  mesh(new THREE.CapsuleGeometry(.115, .27, 4, 8), darkMat, leftLeg, [-.025, -.6, 0], [0, 0, .025]);
  mesh(new THREE.CylinderGeometry(.125, .115, .15, 8), wrapMat, leftLeg, [-.025, -.78, 0]);
  mesh(new THREE.BoxGeometry(.25, .17, .48), primaryMat, leftLeg, [-.025, -.9, .12], [-.06, 0, 0]);
  mesh(new THREE.BoxGeometry(.2, .035, .16), metalMat, leftLeg, [-.025, -.86, .37]);

  const rightLeg = new THREE.Group();
  rightLeg.position.set(.2, .9, 0);
  model.add(rightLeg);
  mesh(new THREE.CapsuleGeometry(.14, .28, 4, 8), secondaryMat, rightLeg, [0, -.18, 0], [0, 0, .035]);
  mesh(new THREE.SphereGeometry(.135, 10, 8), primaryMat, rightLeg, [.015, -.43, .015], [0, 0, 0], [1, .8, .9]);
  mesh(new THREE.CapsuleGeometry(.115, .27, 4, 8), darkMat, rightLeg, [.025, -.6, 0], [0, 0, -.025]);
  mesh(new THREE.CylinderGeometry(.125, .115, .15, 8), wrapMat, rightLeg, [.025, -.78, 0]);
  mesh(new THREE.BoxGeometry(.25, .17, .48), primaryMat, rightLeg, [.025, -.9, .12], [-.06, 0, 0]);
  mesh(new THREE.BoxGeometry(.2, .035, .16), metalMat, rightLeg, [.025, -.86, .37]);

  if (enemy) {
    const sandOrb = mesh(new THREE.IcosahedronGeometry(.34, 1), toon(0xa26034, { roughness: 1 }), model, [side * .38, 1.62, -.35]);
    sandOrb.scale.set(.75, 1.18, .52);
    const ribbon = mesh(new THREE.TorusGeometry(.26, .035, 6, 24, Math.PI * 1.35), accentMat, model, [side * .42, 1.65, -.43], [Math.PI / 2, .2, 0]);
    mesh(new THREE.BoxGeometry(.72, .055, .065), leatherMat, model, [0, 1.4, -.33], [0, 0, side * -.55]);
    mesh(new THREE.BoxGeometry(.24, .13, .13), metalMat, model, [side * -.35, 1.18, .26], [0, side * .1, 0]);
    mesh(new THREE.BoxGeometry(.2, .48, .08), secondaryMat, model, [side * .3, 1.25, -.3], [side * -.15, 0, side * -.2]);
    root.userData.sandOrb = sandOrb;
    root.userData.ribbon = ribbon;
  } else {
    mesh(new THREE.TorusGeometry(.38, .045, 6, 28, Math.PI * 1.6), accentMat, model, [0, 1.76, -.05], [Math.PI / 2, 0, -.2]);
    mesh(new THREE.BoxGeometry(.12, .62, .055), accentMat, model, [-.18, 1.48, -.25], [.18, 0, -.12]);
    mesh(new THREE.BoxGeometry(.12, .52, .055), accentMat, model, [.05, 1.47, -.26], [-.18, 0, .1]);
    mesh(new THREE.CylinderGeometry(.045, .045, .64, 8), leatherMat, model, [-.38, 1.43, -.3], [0, 0, -.52]);
    mesh(new THREE.CylinderGeometry(.075, .065, .18, 8), accentMat, model, [-.54, 1.66, -.3], [0, 0, -.52]);
    mesh(new THREE.BoxGeometry(.22, .34, .11), leatherMat, model, [.37, 1.04, -.08], [0, -.18, 0]);
  }

  const aura = new THREE.Mesh(
    new THREE.RingGeometry(.52, .68, 32),
    new THREE.MeshBasicMaterial({ color: accent, transparent: true, opacity: .16, side: THREE.DoubleSide, depthWrite: false })
  );
  aura.rotation.x = -Math.PI / 2;
  aura.position.y = .02;
  root.add(aura);

  root.userData.parts = { head, leftArm, rightArm, leftLeg, rightLeg, aura };
  root.userData.materials = { primaryMat, secondaryMat, accentMat, hairMat, darkMat };
  return root;
}

const playerModel = createNinja({ side: -1, primary: 0x1d86a5, secondary: 0x14253d, accent: 0x2edbeb, skin: 0xc68665, hair: 0x111b29 });
const enemyModel = createNinja({ side: 1, primary: 0x672a2f, secondary: 0x221a28, accent: 0xd86536, skin: 0xb6795e, hair: 0x311419, enemy: true });
world.add(playerModel, enemyModel);

const effects = new THREE.Group();
world.add(effects);
const energyOrb = mesh(
  new THREE.IcosahedronGeometry(.24, 2),
  toon(0x3de5f3, { emissive: 0x16b9d1, emissiveIntensity: 3, transparent: true, opacity: .9 }),
  effects,
  [0, 0, 0]
);
energyOrb.visible = false;

const windRings = [];
for (let i = 0; i < 4; i++) {
  const wind = mesh(
    new THREE.TorusGeometry(.26 + i * .06, .018, 5, 24),
    new THREE.MeshBasicMaterial({ color: 0x7ddcff, transparent: true, opacity: .72, depthWrite: false }),
    effects,
    [0, 0, 0],
    [0, Math.PI / 2, 0]
  );
  wind.visible = false;
  windRings.push(wind);
}

const shadowClones = [0, 1].map((index) => {
  const clone = createNinja({
    side: -1,
    primary: 0x1d86a5,
    secondary: 0x14253d,
    accent: 0x2edbeb,
    skin: 0xc68665,
    hair: 0x111b29,
  });
  clone.visible = false;
  clone.userData.cloneIndex = index;
  clone.userData.baseX = playerModel.userData.baseX;
  clone.scale.setScalar(.96);
  clone.userData.parts.aura.material.opacity = .32;
  effects.add(clone);
  return clone;
});

const slashArc = mesh(
  new THREE.TorusGeometry(.72, .045, 6, 34, Math.PI * 1.25),
  new THREE.MeshBasicMaterial({ color: 0xb7f8ff, transparent: true, opacity: .9, depthWrite: false }),
  effects,
  [0, 0, 0],
  [0, Math.PI / 2, -.8]
);
slashArc.visible = false;

const cloneSlashArcs = [0, 1].map((index) => {
  const arc = mesh(
    new THREE.TorusGeometry(.58, .035, 6, 30, Math.PI * 1.08),
    new THREE.MeshBasicMaterial({ color: index ? 0x71d9ff : 0x8df8ff, transparent: true, opacity: .9, depthWrite: false }),
    effects,
    [0, 0, 0],
    [0, Math.PI / 2, -.7]
  );
  arc.visible = false;
  return arc;
});

const shockwave = mesh(
  new THREE.RingGeometry(.18, .25, 40),
  new THREE.MeshBasicMaterial({ color: 0x7feef8, transparent: true, opacity: .8, side: THREE.DoubleSide, depthWrite: false }),
  effects,
  [0, 0, 0],
  [0, 0, 0]
);
shockwave.visible = false;

const combatParticles = Array.from({ length: 38 }, (_, index) => {
  const particle = new THREE.Mesh(
    new THREE.IcosahedronGeometry(index % 3 === 0 ? .045 : .025, 0),
    new THREE.MeshBasicMaterial({ color: 0x6ee9f5, transparent: true, opacity: 0, depthWrite: false })
  );
  particle.visible = false;
  particle.userData.velocity = new THREE.Vector3();
  particle.userData.life = 0;
  effects.add(particle);
  return particle;
});

let cameraShake = 0;
const previousShake = new THREE.Vector3();

function burstParticles(position, color, amount = 22, force = 1) {
  if (state?.settings?.reducedEffects) amount = Math.max(3, Math.round(amount * .35));
  const pool = [
    ...combatParticles.filter((particle) => !particle.visible),
    ...combatParticles.filter((particle) => particle.visible).sort((a, b) => a.userData.life - b.userData.life),
  ].slice(0, Math.min(amount, combatParticles.length));
  pool.forEach((particle, index) => {
    const angle = (index / amount) * Math.PI * 2 + Math.random() * .35;
    const lift = .3 + Math.random() * 1.1;
    particle.visible = true;
    particle.position.copy(position);
    particle.material.color.setHex(color);
    particle.material.opacity = .9;
    particle.scale.setScalar(.7 + Math.random() * .8);
    particle.userData.velocity.set(
      Math.cos(angle) * (.45 + Math.random()) * force,
      lift * force,
      Math.sin(angle) * (.35 + Math.random() * .8) * force
    );
    particle.userData.life = .45 + Math.random() * .36;
  });
}

function updateParticles(delta) {
  combatParticles.forEach((particle) => {
    if (!particle.visible) return;
    particle.userData.life -= delta;
    particle.userData.velocity.y -= delta * 2.5;
    particle.position.addScaledVector(particle.userData.velocity, delta);
    particle.rotation.x += delta * 9;
    particle.rotation.y += delta * 7;
    particle.material.opacity = Math.max(0, particle.userData.life * 1.8);
    if (particle.userData.life <= 0) particle.visible = false;
  });
}

// Explorable overworld -------------------------------------------------------
const mapWorld = new THREE.Group();
scene.add(mapWorld);
world.visible = false;

const mapGround = mesh(
  new THREE.PlaneGeometry(30, 24),
  toon(0x20382f, { roughness: 1 }),
  mapWorld,
  [0, -.08, 0],
  [-Math.PI / 2, 0, 0]
);
mapGround.receiveShadow = true;

const pathMaterial = toon(0x6b6355, { roughness: 1 });
mesh(new THREE.PlaneGeometry(5.2, 23.6), pathMaterial, mapWorld, [0, -.055, 0], [-Math.PI / 2, 0, 0]);
mesh(new THREE.PlaneGeometry(29.4, 3.6), pathMaterial, mapWorld, [0, -.05, 1.6], [-Math.PI / 2, 0, 0]);
mesh(new THREE.CircleGeometry(4.2, 36), toon(0x5c584e), mapWorld, [0, -.042, 1.6], [-Math.PI / 2, 0, 0]);

const creek = mesh(
  new THREE.PlaneGeometry(2.2, 24),
  toon(0x17475b, { emissive: 0x0d2834, emissiveIntensity: .25, transparent: true, opacity: .9 }),
  mapWorld,
  [-8.6, -.035, 0],
  [-Math.PI / 2, 0, -.08]
);
creek.receiveShadow = false;
const mapColliders = [];

function canWalkTo(x, z) {
  return mapColliders.every((collider) => Math.hypot(x - collider.x, z - collider.z) > collider.radius + .34);
}

function createTree(x, z, scale = 1) {
  const tree = new THREE.Group();
  tree.position.set(x, 0, z);
  tree.scale.setScalar(scale);
  mesh(new THREE.CylinderGeometry(.13, .2, 1.25, 7), toon(0x382b25), tree, [0, .56, 0]);
  mesh(new THREE.ConeGeometry(.78, 1.55, 8), toon(0x18352f), tree, [0, 1.42, 0]);
  mesh(new THREE.ConeGeometry(.61, 1.28, 8), toon(0x254a3d), tree, [0, 2.04, 0]);
  mapWorld.add(tree);
  mapColliders.push({ x, z, radius: .36 * scale });
}

[
  [-12, -9, 1.25], [-10.8, -6.8, .9], [-12.5, -4.2, 1.05], [-10.7, -1.8, 1.2],
  [-12.1, 2.4, 1], [-10.8, 5.4, 1.18], [-12.5, 8.7, 1.3], [-6.3, -9.6, 1.1],
  [-4.8, -8.2, .84], [5.6, -9.4, 1.12], [8.5, -8.5, .9], [11.9, -8.9, 1.25],
  [12.6, -5.8, .94], [11.5, -2.8, 1.2], [12.6, .4, .88], [11.8, 4.5, 1.25],
  [12.5, 8.6, 1.05], [7.1, 9.3, .92], [4.9, 10.2, 1.15], [-4.6, 10, 1.1],
  [-7.2, 9.1, .85], [-5.7, -4.4, .72], [6.5, -4.8, .82], [8.4, 5.9, .78],
].forEach(([x, z, scale]) => createTree(x, z, scale));

function createHouse(x, z, color = 0x394757, rotation = 0) {
  const house = new THREE.Group();
  house.position.set(x, 0, z);
  house.rotation.y = rotation;
  mesh(new THREE.BoxGeometry(2.4, 1.55, 2), toon(color), house, [0, .75, 0]);
  mesh(new THREE.ConeGeometry(1.85, .78, 4), toon(0x642e32), house, [0, 1.86, 0], [0, Math.PI / 4, 0], [1, 1, .82]);
  mesh(new THREE.BoxGeometry(.58, 1, .08), toon(0x241b1b), house, [0, .5, 1.04]);
  mesh(new THREE.BoxGeometry(.42, .42, .06), toon(0xf09c42, { emissive: 0xa04a18, emissiveIntensity: 1.8 }), house, [-.72, .92, 1.06]);
  mapWorld.add(house);
  mapColliders.push({ x, z, radius: 1.42 });
}

createHouse(-5.7, -6.8, 0x314653, .12);
createHouse(5.9, -6.6, 0x463a4f, -.14);
createHouse(-6.3, 6.5, 0x394958, -.08);
createHouse(6.1, 6.8, 0x34434d, .1);

function createRock(x, z, scale = 1) {
  const rock = mesh(new THREE.DodecahedronGeometry(.6, 0), toon(0x4b5660), mapWorld, [x, .24 * scale, z], [0, x * .17, 0], [scale, .65 * scale, .8 * scale]);
  rock.receiveShadow = true;
  mapColliders.push({ x, z, radius: .48 * scale });
}
[
  [-9.8, -9, 1], [-7.7, 7.9, .8], [9.2, -6, .65], [9.6, 8.4, 1.1], [-5.1, 4, .6], [5.1, -2.5, .72],
].forEach(([x, z, scale]) => createRock(x, z, scale));

// Village gate and warm guide lights.
[-1.35, 1.35].forEach((x) => mesh(new THREE.BoxGeometry(.25, 2.5, .25), toon(0x5d2d2d), mapWorld, [x, 1.16, -10.1]));
mesh(new THREE.BoxGeometry(3.35, .28, .38), toon(0x6f3232), mapWorld, [0, 2.32, -10.1]);
mesh(new THREE.BoxGeometry(2.8, .2, .3), toon(0x7c3935), mapWorld, [0, 2.7, -10.1]);

const mapPlayer = createNinja({ side: -1, primary: 0x1d86a5, secondary: 0x14253d, accent: 0x2edbeb, skin: 0xc68665, hair: 0x111b29 });
mapPlayer.userData.baseX = 0;
mapPlayer.position.set(0, 0, 8.2);
mapPlayer.scale.setScalar(.6);
mapPlayer.rotation.y = Math.PI;
mapWorld.add(mapPlayer);

function createNameSprite(text) {
  const labelCanvas = document.createElement('canvas');
  labelCanvas.width = 512;
  labelCanvas.height = 96;
  const context = labelCanvas.getContext('2d');
  context.fillStyle = 'rgba(6, 12, 20, .88)';
  context.fillRect(34, 12, 444, 66);
  context.strokeStyle = '#e06c44';
  context.lineWidth = 4;
  context.strokeRect(34, 12, 444, 66);
  context.fillStyle = '#f5e8df';
  context.font = '700 31px Arial';
  context.textAlign = 'center';
  context.fillText(text, 256, 56);
  const texture = new THREE.CanvasTexture(labelCanvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  const sprite = new THREE.Sprite(new THREE.SpriteMaterial({ map: texture, transparent: true, depthTest: false }));
  sprite.scale.set(2.75, .52, 1);
  sprite.position.y = 2.6;
  return sprite;
}

const enemySeeds = [
  { id: 'north', name: 'BATEDOR DA NÉVOA', x: -3.9, z: -7.3, archetype: 'scout' },
  { id: 'creek', name: 'CANALIZADOR ERRANTE', x: -7.1, z: 2.5, archetype: 'channeler' },
  { id: 'east', name: 'GUARDA ESCARLATE', x: 7.4, z: 3.4, archetype: 'guardian' },
  { id: 'south', name: 'CAÇADOR SOMBRIO', x: 3.8, z: 8.3, archetype: 'hunter' },
  { id: 'west', name: 'VIGIA DA MARÉ', x: -10.1, z: -3.2, archetype: 'channeler' },
  { id: 'ridge', name: 'SENTINELA MINERAL', x: 9.7, z: -3.7, archetype: 'guardian' },
  { id: 'warden', name: 'REGENTE DO ECLIPSE', x: 0, z: -8.3, archetype: 'warden', bossOnly: true },
];
let mapEnemies = [];
let currentEnemy = null;
let enemyWave = 1;

function spawnWorldEnemies(level = 1) {
  mapEnemies.forEach((entry) => mapWorld.remove(entry.model));
  const activeSeeds = enemySeeds.filter((seed) => !seed.bossOnly || enemyWave % 2 === 0);
  mapEnemies = activeSeeds.map((seed, index) => {
    const archetype = ENEMY_ARCHETYPES[seed.archetype];
    const affinity = AFFINITIES[archetype.affinity];
    const model = createNinja({
      side: 1,
      primary: index % 2 ? 0x56323a : 0x6b2b31,
      secondary: 0x211a24,
      accent: affinity.hex,
      skin: 0xb6795e,
      hair: 0x28151a,
      enemy: index % 2 === 0,
    });
    model.position.set(seed.x, 0, seed.z);
    model.scale.setScalar(archetype.boss ? .76 : .58);
    model.rotation.y = Math.PI;
    model.userData.baseX = seed.x;
    model.add(createNameSprite(`${seed.name}  •  NV. ${level + index}  •  ${affinity.name}`));
    const alertRing = new THREE.Mesh(
      new THREE.RingGeometry(.56, .73, 28),
      new THREE.MeshBasicMaterial({ color: affinity.hex, transparent: true, opacity: .62, side: THREE.DoubleSide })
    );
    alertRing.rotation.x = -Math.PI / 2;
    alertRing.position.y = .035;
    model.add(alertRing);
    model.userData.alertRing = alertRing;
    mapWorld.add(model);
    return { ...archetype, ...seed, level: level + index, model, defeated: false, originX: seed.x, originZ: seed.z, patrolPhase: index * 1.7 };
  });
}

const characters = CHARACTERS;
const savedProgress = loadProgress();

const startingLevel = Math.max(1, Number(savedProgress.level) || 1);
const savedCharacter = characters[savedProgress.character] ? savedProgress.character : 'kaito';
const savedStats = { atk: 0, def: 0, spd: 0, chakra: 0, ...(savedProgress.stats || {}) };
const initialClan = CLANS[savedProgress.clan] || CLANS.arashi;
const startingMaxHp = 1240 + (startingLevel - 1) * 95 + savedStats.def * 55 + characters[savedCharacter].bonuses.hp + initialClan.bonuses.hp;
const state = {
  mode: 'world',
  turn: 1,
  round: 1,
  phase: 'player',
  selected: 'attack',
  locked: false,
  gameOver: false,
  sound: savedProgress.settings.sound,
  level: startingLevel,
  xp: Math.max(0, Number(savedProgress.xp) || 0),
  coins: Math.max(0, Number(savedProgress.coins) || 0),
  statPoints: Math.max(0, Number(savedProgress.statPoints) || 0),
  powerPoints: Math.max(0, Number(savedProgress.powerPoints) || 0),
  stats: savedStats,
  character: savedCharacter,
  clan: savedProgress.clan,
  affinity: savedProgress.affinity,
  mastery: savedProgress.mastery,
  skillRanks: savedProgress.skillRanks,
  records: savedProgress.records,
  achievements: savedProgress.achievements,
  settings: savedProgress.settings,
  unlockedCharacters: Array.isArray(savedProgress.unlockedCharacters) ? [...new Set(['kaito', ...savedProgress.unlockedCharacters])] : ['kaito'],
  player: {
    hp: startingMaxHp,
    maxHp: startingMaxHp,
    chakra: 520,
    maxChakra: 600 + savedStats.chakra * 35 + characters[savedCharacter].bonuses.chakra + initialClan.bonuses.chakra,
    guard: false,
    status: null,
  },
  enemy: { hp: 1180, maxHp: 1180, chakra: 480, maxChakra: 600, guard: false, affinity: 'stone', status: null },
};

$('#app').classList.add('world-mode');
spawnWorldEnemies(state.level);

function saveProgress() {
  saveProgressData({
    level: state.level,
    xp: state.xp,
    coins: state.coins,
    statPoints: state.statPoints,
    powerPoints: state.powerPoints,
    stats: state.stats,
    character: state.character,
    clan: state.clan,
    affinity: state.affinity,
    mastery: state.mastery,
    skillRanks: state.skillRanks,
    records: state.records,
    achievements: state.achievements,
    settings: state.settings,
    unlockedCharacters: state.unlockedCharacters,
  });
}

function currentCharacter() {
  return characters[state.character] || characters.kaito;
}

function currentClan() {
  return CLANS[state.clan] || CLANS.arashi;
}

function currentAffinity() {
  return AFFINITIES[state.affinity] || AFFINITIES.gale;
}

function currentMastery() {
  return state.mastery[state.affinity];
}

function combinedBonuses() {
  const hero = currentCharacter().bonuses;
  const clan = currentClan().bonuses;
  return {
    atk: hero.atk + clan.atk,
    def: hero.def + clan.def,
    spd: hero.spd + clan.spd,
    hp: hero.hp + clan.hp,
    chakra: hero.chakra + clan.chakra,
    mastery: clan.mastery,
  };
}

const achievementDefinitions = [
  { id: 'first_victory', label: 'PRIMEIRO CONTRATO', reward: 120, unlocked: () => state.records.victories >= 1 },
  { id: 'veteran', label: 'VETERANO DA FRONTEIRA', reward: 350, unlocked: () => state.records.victories >= 10 },
  { id: 'boss_hunter', label: 'CAÇADOR DE REGENTES', reward: 500, unlocked: () => state.records.bossesDefeated >= 1 },
  { id: 'master', label: 'MESTRE ELEMENTAL', reward: 700, unlocked: () => Object.values(state.mastery).some((entry) => entry.level >= 5) },
];

function unlockAchievements() {
  const unlockedNow = achievementDefinitions.filter((achievement) => !state.achievements.includes(achievement.id) && achievement.unlocked());
  unlockedNow.forEach((achievement) => {
    state.achievements.push(achievement.id);
    state.coins += achievement.reward;
  });
  return unlockedNow;
}

function applyCharacterVisuals() {
  const character = currentCharacter();
  [playerModel, mapPlayer, ...shadowClones].forEach((model) => {
    const materials = model.userData.materials;
    materials.primaryMat.color.setHex(character.palette.primary);
    materials.secondaryMat.color.setHex(character.palette.secondary);
    materials.accentMat.color.setHex(character.palette.accent);
    materials.accentMat.emissive?.setHex(character.palette.accent);
    materials.hairMat.color.setHex(character.palette.hair);
    model.userData.parts.aura.material.color.setHex(currentAffinity().hex);
  });
  $('#playerName').textContent = character.name;
  $('#mapPlayerName').textContent = character.name;
  document.documentElement.style.setProperty('--affinity', currentAffinity().color);
}

async function loadSelectedRiggedModels() {
  const specification = currentCharacter().model;
  if (!specification) return;
  try {
    await Promise.all([
      attachRiggedModel(playerModel, { ...specification, position: [.32, 0, 0], rotationY: Math.PI / 2 }),
      attachRiggedModel(mapPlayer, specification),
      ...shadowClones.map((model) => attachRiggedModel(model, { ...specification, position: [.22, 0, 0], rotationY: Math.PI / 2 })),
    ]);
  } catch (error) {
    console.warn('Modelo rigado indisponível; mantendo o modelo procedural.', error);
  }
}

function recalculatePlayerCaps(preserveRatio = true) {
  const hpRatio = state.player.maxHp ? state.player.hp / state.player.maxHp : 1;
  const chakraRatio = state.player.maxChakra ? state.player.chakra / state.player.maxChakra : 1;
  const bonuses = combinedBonuses();
  state.player.maxHp = 1240 + (state.level - 1) * 95 + state.stats.def * 55 + bonuses.hp;
  state.player.maxChakra = 600 + state.stats.chakra * 35 + bonuses.chakra;
  state.player.hp = preserveRatio ? Math.max(1, Math.round(state.player.maxHp * hpRatio)) : state.player.maxHp;
  state.player.chakra = preserveRatio ? Math.round(state.player.maxChakra * chakraRatio) : state.player.maxChakra;
}

function renderProgressionMenu() {
  $('#menuCoins').textContent = formatNumber(state.coins);
  $('#menuPoints').textContent = state.statPoints;
  $('#menuPowerPoints').textContent = state.powerPoints;
  $('#statAtkValue').textContent = state.stats.atk;
  $('#statDefValue').textContent = state.stats.def;
  $('#statSpdValue').textContent = state.stats.spd;
  $('#statChkValue').textContent = state.stats.chakra;
  $$('[data-upgrade]').forEach((button) => { button.disabled = state.statPoints <= 0; });
  $('#clanChoices').innerHTML = Object.entries(CLANS).map(([id, clan]) => `
    <button type="button" class="build-choice ${state.clan === id ? 'selected' : ''}" data-clan="${id}">
      <b>${clan.name.replace('Clã ', '')}</b><small>${clan.motto}</small>
    </button>`).join('');
  $('#affinityChoices').innerHTML = Object.entries(AFFINITIES).map(([id, affinity]) => `
    <button type="button" class="build-choice ${state.affinity === id ? 'selected' : ''}" data-affinity="${id}" style="--choice-color:${affinity.color}">
      <b>${affinity.icon} ${affinity.name}</b><small>${affinity.description}</small>
    </button>`).join('');
  const mastery = state.mastery[state.affinity];
  $('#masteryName').textContent = currentAffinity().name.toUpperCase();
  $('#masteryLevel').textContent = mastery.level;
  $('#masteryText').textContent = `${mastery.xp} / ${masteryNeeded(mastery.level)} DE DOMÍNIO`;
  $('#masteryBar').style.width = `${Math.min(100, mastery.xp / masteryNeeded(mastery.level) * 100)}%`;
  $('#powerChoices').innerHTML = Object.entries(actions)
    .filter(([id, action]) => action.min && id !== 'attack')
    .map(([id, action]) => {
      const atLimit = action.rank >= 5;
      const masteryBlocked = action.rank >= mastery.level + 1;
      const disabled = atLimit || masteryBlocked || state.powerPoints <= 0;
      return `<div class="power-upgrade"><div><b>${action.label}</b><small>RANK ${action.rank} • ${action.mastery > mastery.level ? `LIBERA NO DOMÍNIO ${action.mastery}` : `+9% DANO • -3 ENERGIA`}</small></div><button type="button" data-power="${id}" ${disabled ? 'disabled' : ''}>${atLimit ? 'MAX' : '+'}</button></div>`;
    }).join('');
  $$('[data-setting]').forEach((input) => { input.checked = Boolean(state.settings[input.dataset.setting]); });
  $('#characterShop').innerHTML = Object.entries(characters).map(([id, character]) => {
    const unlocked = state.unlockedCharacters.includes(id);
    const equipped = state.character === id;
    const bonusLabels = [];
    if (character.bonuses.atk) bonusLabels.push(`ATQ ${character.bonuses.atk > 0 ? '+' : ''}${Math.round(character.bonuses.atk * 100)}%`);
    if (character.bonuses.def) bonusLabels.push(`DEF +${Math.round(character.bonuses.def * 100)}%`);
    if (character.bonuses.spd) bonusLabels.push(`VEL ${character.bonuses.spd > 0 ? '+' : ''}${Math.round(character.bonuses.spd * 100)}%`);
    if (character.bonuses.hp) bonusLabels.push(`VIDA +${character.bonuses.hp}`);
    if (character.bonuses.chakra) bonusLabels.push(`ENERGIA +${character.bonuses.chakra}`);
    return `<article class="character-card ${equipped ? 'equipped' : ''}">
      <span>${character.title} • ${CLANS[character.clan].name}</span><i class="char-mark">${character.mark}</i><h3>${character.name}</h3>
      <p>${character.description}</p><div class="character-bonuses">${bonusLabels.length ? bonusLabels.map((label) => `<em>${label}</em>`).join('') : '<em>EQUILIBRADO</em>'}</div>
      <button type="button" data-character="${id}" ${equipped ? 'disabled' : ''}>${equipped ? 'EQUIPADO' : unlocked ? 'EQUIPAR' : `COMPRAR • ${character.price} RYO`}</button>
    </article>`;
  }).join('');
}

function openProgressionMenu(section) {
  Object.keys(moveInput).forEach((key) => { moveInput[key] = false; });
  mapVelocity.set(0, 0, 0);
  $('#progressionTitle').textContent = section === 'shop' ? 'PERSONAGENS E FORMAÇÕES' : 'EVOLUIR LUTADOR';
  renderProgressionMenu();
  $('#progressionOverlay').classList.add('show');
  $('#progressionOverlay').setAttribute('aria-hidden', 'false');
  requestAnimationFrame(() => (section === 'shop' ? $('#characterShop') : $('#statusShop')).scrollIntoView({ block: 'nearest' }));
}

function closeProgressionMenu() {
  $('#progressionOverlay').classList.remove('show');
  $('#progressionOverlay').setAttribute('aria-hidden', 'true');
}

$('#statusMenuBtn').addEventListener('click', () => openProgressionMenu('status'));
$('#shopMenuBtn').addEventListener('click', () => openProgressionMenu('shop'));
$('#closeProgressionBtn').addEventListener('click', closeProgressionMenu);
$('#progressionOverlay').addEventListener('click', (event) => {
  if (event.target === $('#progressionOverlay')) closeProgressionMenu();
  const clanButton = event.target.closest('[data-clan]');
  const affinityButton = event.target.closest('[data-affinity]');
  const powerButton = event.target.closest('[data-power]');
  const settingInput = event.target.closest('[data-setting]');
  const saveButton = event.target.closest('[data-save-action]');
  if (clanButton) {
    state.clan = clanButton.dataset.clan;
    recalculatePlayerCaps(true);
    saveProgress();
    renderProgressionMenu();
    updateUI();
    tone(390, .1, 'triangle');
  }
  if (affinityButton) {
    state.affinity = affinityButton.dataset.affinity;
    state.selected = 'attack';
    refreshActions();
    applyCharacterVisuals();
    saveProgress();
    renderProgressionMenu();
    updateUI();
    tone(470, .13, 'sine');
  }
  if (powerButton && !powerButton.disabled && state.powerPoints > 0) {
    state.powerPoints -= 1;
    state.skillRanks[powerButton.dataset.power] = Math.min(5, state.skillRanks[powerButton.dataset.power] + 1);
    refreshActions();
    saveProgress();
    renderProgressionMenu();
    updateUI();
    tone(660, .16, 'sine');
  }
  if (settingInput) {
    state.settings[settingInput.dataset.setting] = settingInput.checked;
    state.sound = state.settings.sound;
    $('#soundBtn').textContent = state.sound ? '◖' : '×';
    $('#soundBtn').setAttribute('aria-pressed', String(state.sound));
    saveProgress();
  }
  if (saveButton?.dataset.saveAction === 'export') {
    saveProgress();
    const blob = new Blob([localStorage.getItem(SAVE_KEY)], { type: 'application/json' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `shinobi-duel-save-nivel-${state.level}.json`;
    link.click();
    URL.revokeObjectURL(link.href);
  }
  if (saveButton?.dataset.saveAction === 'import') $('#saveFileInput').click();
});
$('#saveFileInput').addEventListener('change', async (event) => {
  const [file] = event.target.files;
  if (!file) return;
  try {
    const imported = migrateSave(JSON.parse(await file.text()));
    saveProgressData(imported);
    window.location.reload();
  } catch {
    $('#masteryText').textContent = 'ARQUIVO DE SAVE INVÁLIDO';
    tone(90, .18, 'square');
  }
});
$$('[data-upgrade]').forEach((button) => button.addEventListener('click', () => {
  if (state.statPoints <= 0) return;
  state.statPoints -= 1;
  state.stats[button.dataset.upgrade] += 1;
  recalculatePlayerCaps(true);
  saveProgress();
  renderProgressionMenu();
  updateUI();
  tone(520, .1, 'sine');
}));
$('#characterShop').addEventListener('click', (event) => {
  const button = event.target.closest('[data-character]');
  if (!button) return;
  const id = button.dataset.character;
  const character = characters[id];
  if (!state.unlockedCharacters.includes(id)) {
    if (state.coins < character.price) {
      tone(90, .15, 'square');
      return;
    }
    state.coins -= character.price;
    state.unlockedCharacters.push(id);
  }
  state.character = id;
  recalculatePlayerCaps(true);
  applyCharacterVisuals();
  loadSelectedRiggedModels();
  saveProgress();
  renderProgressionMenu();
  updateUI();
  tone(620, .16, 'sine');
});

let actions = createActions(state.affinity, state.skillRanks);

function refreshActions() {
  actions = createActions(state.affinity, state.skillRanks);
  $$('.move-card').forEach((button) => {
    const action = actions[button.dataset.action];
    if (!action) return;
    button.querySelector('.move-copy b').textContent = action.label.toUpperCase();
    button.querySelector('.move-copy small').textContent = action.description;
    button.querySelector('.cost').textContent = action.cost;
    if (button.dataset.action === 'wind') button.querySelector('.move-icon').textContent = currentAffinity().icon;
  });
}

refreshActions();
applyCharacterVisuals();
recalculatePlayerCaps(true);

let actionAnimation = null;
let cameraAngle = 0;
let cameraLift = 0;
let isDragging = false;
let lastPointer = { x: 0, y: 0 };
let audioContext = null;
const moveInput = { up: false, down: false, left: false, right: false };
const mapRaycaster = new THREE.Raycaster();
const mapPointer = new THREE.Vector2();
const mapVelocity = new THREE.Vector3();
let mapTarget = null;
let encounterCooldown = 0;
let walkPhase = 0;
const MAP_ZOOM_MIN = .68;
const MAP_ZOOM_MAX = 1.65;
let mapZoom = 1;
let targetMapZoom = 1;
const worldTouchPointers = new Map();
let pinchStartDistance = 0;
let pinchStartZoom = 1;

function updateMapZoomUI() {
  const value = $('#zoomValue');
  if (value) value.textContent = `${Math.round(100 / targetMapZoom)}%`;
}

function setMapZoom(value) {
  targetMapZoom = THREE.MathUtils.clamp(value, MAP_ZOOM_MIN, MAP_ZOOM_MAX);
  updateMapZoomUI();
}

function mapCameraOffset(zoom = mapZoom) {
  return new THREE.Vector3(7.2 * zoom, 7.6 * zoom, 9.2 * zoom);
}

function setCamera() {
  if (state.mode === 'world') {
    const offset = mapCameraOffset();
    camera.position.copy(mapPlayer.position).add(offset);
    camera.lookAt(mapPlayer.position.x, .6, mapPlayer.position.z - 1.2 * mapZoom);
    return;
  }
  const radius = window.innerWidth < 680 ? 11.9 : 10.6;
  const targetY = window.innerWidth < 680 ? 1.18 : 1.3;
  camera.position.set(Math.sin(cameraAngle) * radius, 3.0 + cameraLift, Math.cos(cameraAngle) * radius);
  camera.lookAt(0, targetY, 0);
}
setCamera();

function resize() {
  const rect = battlefield.getBoundingClientRect();
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, window.innerWidth < 680 ? 1.45 : 1.75));
  renderer.setSize(rect.width, rect.height, false);
  camera.aspect = rect.width / rect.height;
  camera.fov = window.innerWidth < 680 ? 42 : 34;
  camera.updateProjectionMatrix();
  setCamera();
}
window.addEventListener('resize', resize);
resize();

canvas.addEventListener('pointerdown', (event) => {
  if (state.mode === 'world') {
    if (event.pointerType === 'touch') {
      worldTouchPointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
      canvas.setPointerCapture?.(event.pointerId);
      if (worldTouchPointers.size >= 2) {
        const [first, second] = [...worldTouchPointers.values()];
        pinchStartDistance = Math.hypot(second.x - first.x, second.y - first.y);
        pinchStartZoom = targetMapZoom;
        mapTarget = null;
        event.preventDefault();
        return;
      }
    }
    const rect = canvas.getBoundingClientRect();
    mapPointer.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
    mapPointer.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;
    mapRaycaster.setFromCamera(mapPointer, camera);
    const hit = mapRaycaster.intersectObject(mapGround)[0];
    if (hit) {
      mapTarget = hit.point.clone();
      mapTarget.x = THREE.MathUtils.clamp(mapTarget.x, -13.2, 13.2);
      mapTarget.z = THREE.MathUtils.clamp(mapTarget.z, -10.7, 10.7);
    }
    return;
  }
  isDragging = true;
  lastPointer = { x: event.clientX, y: event.clientY };
  canvas.setPointerCapture(event.pointerId);
  $('#cameraHint').style.opacity = '0';
});
canvas.addEventListener('pointermove', (event) => {
  if (state.mode === 'world') {
    if (event.pointerType === 'touch' && worldTouchPointers.has(event.pointerId)) {
      worldTouchPointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
      if (worldTouchPointers.size >= 2 && pinchStartDistance > 0) {
        const [first, second] = [...worldTouchPointers.values()];
        const currentDistance = Math.hypot(second.x - first.x, second.y - first.y);
        if (currentDistance > 0) setMapZoom(pinchStartZoom * pinchStartDistance / currentDistance);
        mapTarget = null;
        event.preventDefault();
      }
    }
    return;
  }
  if (!isDragging) return;
  cameraAngle = THREE.MathUtils.clamp(cameraAngle - (event.clientX - lastPointer.x) * .004, -.42, .42);
  cameraLift = THREE.MathUtils.clamp(cameraLift + (event.clientY - lastPointer.y) * .003, -.45, .7);
  lastPointer = { x: event.clientX, y: event.clientY };
  setCamera();
});
function finishCanvasPointer(event) {
  isDragging = false;
  worldTouchPointers.delete(event.pointerId);
  if (worldTouchPointers.size < 2) pinchStartDistance = 0;
}
canvas.addEventListener('pointerup', finishCanvasPointer);
canvas.addEventListener('pointercancel', finishCanvasPointer);

canvas.addEventListener('wheel', (event) => {
  if (state.mode !== 'world') return;
  event.preventDefault();
  const direction = Math.sign(event.deltaY);
  setMapZoom(targetMapZoom + direction * .12);
}, { passive: false });

$('#zoomInBtn').addEventListener('click', () => setMapZoom(targetMapZoom - .15));
$('#zoomOutBtn').addEventListener('click', () => setMapZoom(targetMapZoom + .15));
$('#zoomResetBtn').addEventListener('click', () => setMapZoom(1));
updateMapZoomUI();

$('#resetCameraBtn').addEventListener('click', () => {
  cameraAngle = 0;
  cameraLift = 0;
  setCamera();
});

function formatNumber(number) {
  return Math.max(0, Math.round(number)).toLocaleString('pt-BR');
}

function setBar(selector, value, max) {
  $(selector).style.width = `${Math.max(0, Math.min(100, value / max * 100))}%`;
}

function updateUI() {
  $('#turnNumber').textContent = state.turn;
  $('#roundNumber').textContent = state.round;
  $('#playerHpText').textContent = `${formatNumber(state.player.hp)} / ${formatNumber(state.player.maxHp)}`;
  $('#playerChakraText').textContent = `${formatNumber(state.player.chakra)} / ${formatNumber(state.player.maxChakra)}`;
  $('#enemyHpText').textContent = `${formatNumber(state.enemy.hp)} / ${formatNumber(state.enemy.maxHp)}`;
  $('#enemyChakraText').textContent = `${formatNumber(state.enemy.chakra)} / ${formatNumber(state.enemy.maxChakra)}`;
  $('#playerLevel').textContent = `Nv. ${state.level}`;
  $('#mapLevel').textContent = `NÍVEL ${state.level}`;
  $('#xpText').textContent = `${state.xp} / ${xpNeeded(state.level)} XP`;
  $('#xpBar').style.width = `${Math.min(100, state.xp / xpNeeded(state.level) * 100)}%`;
  $('#mapClan').textContent = currentClan().name.toUpperCase();
  $('#mapAffinity').textContent = `${currentAffinity().icon} ${currentAffinity().name.toUpperCase()} • DOMÍNIO ${currentMastery().level}`;
  $('#playerAffinity').textContent = currentAffinity().name.toUpperCase();
  const enemyAffinity = AFFINITIES[state.enemy.affinity] || AFFINITIES.stone;
  $('#enemyAffinity').textContent = enemyAffinity.name.toUpperCase();
  $('#enemyAffinity').style.color = enemyAffinity.color;
  $('#coinText').textContent = formatNumber(state.coins);
  $('#statPointBadge').textContent = state.statPoints;
  $('#statPointBadge').style.display = state.statPoints > 0 ? 'inline-grid' : 'none';
  const aliveEnemies = mapEnemies.filter((enemy) => !enemy.defeated).length;
  $('#enemyCounter').textContent = `${aliveEnemies} ${aliveEnemies === 1 ? 'INIMIGO RESTANTE' : 'INIMIGOS RESTANTES'}`;
  const bossAlive = mapEnemies.some((enemy) => enemy.boss && !enemy.defeated);
  $('#missionText').textContent = bossAlive ? 'DERROTE O REGENTE' : aliveEnemies ? `PROTEJA A VILA • ONDA ${enemyWave}` : 'ÁREA PROTEGIDA';
  setBar('#playerHpBar', state.player.hp, state.player.maxHp);
  setBar('#playerChakraBar', state.player.chakra, state.player.maxChakra);
  setBar('#enemyHpBar', state.enemy.hp, state.enemy.maxHp);
  setBar('#enemyChakraBar', state.enemy.chakra, state.enemy.maxChakra);

  const playerTurn = state.phase === 'player';
  $('#turnLabel').textContent = playerTurn ? 'SEU TURNO' : 'TURNO INIMIGO';
  $('#turnPill').classList.toggle('enemy', !playerTurn);
  $('#confirmBtn').disabled = state.locked || !playerTurn || state.gameOver;

  $$('.move-card, .utility-btn').forEach((button) => {
    const data = actions[button.dataset.action];
    button.classList.toggle('active', button.dataset.action === state.selected);
    const masteryLocked = (data.mastery || 0) > currentMastery().level;
    button.classList.toggle('mastery-locked', masteryLocked);
    button.disabled = state.locked || !playerTurn || state.gameOver || masteryLocked || (data.cost > state.player.chakra);
    button.title = masteryLocked ? `Requer domínio ${data.mastery} de ${currentAffinity().name}` : data.description || '';
  });

  const selectedStats = actions[state.selected]?.stats || actions.attack.stats;
  const bonuses = combinedBonuses();
  $('#playerAtk').textContent = Math.round((selectedStats[0] + state.stats.atk * 9) * (1 + bonuses.atk));
  $('#playerDef').textContent = Math.round((selectedStats[1] + state.stats.def * 8) * (1 + bonuses.def));
  $('#playerSpd').textContent = Math.round((selectedStats[2] + state.stats.spd * 7) * (1 + bonuses.spd));
  updateTimeline();
}

function updateTimeline() {
  const units = $$('.timeline-unit');
  const playerTurn = state.phase === 'player';
  const order = playerTurn
    ? [['K', 'Kaito Arashi', 'AGORA'], ['R', 'Ren Hayashi', 'PRÓXIMO'], ['K', 'Kaito Arashi', 'DEPOIS']]
    : [['R', 'Ren Hayashi', 'AGORA'], ['K', 'Kaito Arashi', 'PRÓXIMO'], ['R', 'Ren Hayashi', 'DEPOIS']];
  units.forEach((unit, index) => {
    const [letter, name, label] = order[index];
    const avatar = unit.querySelector('.avatar');
    avatar.textContent = letter;
    avatar.className = `avatar avatar-${letter.toLowerCase()}`;
    unit.querySelector('b').textContent = name;
    unit.querySelector('small').textContent = label;
  });
}

function log(message, actor = 'KAITO') {
  $('#battleLog').innerHTML = `<b>${actor}</b> ${message}`;
}

function randomBetween(min, max) {
  return Math.round(min + Math.random() * (max - min));
}

function tone(frequency = 180, duration = .1, type = 'sine') {
  if (!state.sound) return;
  audioContext ||= new (window.AudioContext || window.webkitAudioContext)();
  const oscillator = audioContext.createOscillator();
  const gain = audioContext.createGain();
  oscillator.type = type;
  oscillator.frequency.value = frequency;
  gain.gain.setValueAtTime(.08, audioContext.currentTime);
  gain.gain.exponentialRampToValueAtTime(.001, audioContext.currentTime + duration);
  oscillator.connect(gain).connect(audioContext.destination);
  oscillator.start();
  oscillator.stop(audioContext.currentTime + duration);
}

$('#soundBtn').addEventListener('click', () => {
  state.sound = !state.sound;
  state.settings.sound = state.sound;
  $('#soundBtn').textContent = state.sound ? '◖' : '×';
  $('#soundBtn').setAttribute('aria-pressed', String(state.sound));
  saveProgress();
  if (state.sound) tone(440, .08);
});

function flashImpact(enemyHit = true) {
  if (state.settings.reducedEffects) return;
  const flash = $('#impactFlash');
  flash.style.background = enemyHit
    ? 'radial-gradient(circle at 62% 55%, rgba(93,239,255,.42), transparent 35%)'
    : 'radial-gradient(circle at 38% 55%, rgba(255,139,74,.4), transparent 35%)';
  flash.classList.remove('show');
  void flash.offsetWidth;
  flash.classList.add('show');
}

function showCombatNumber(amount, targetActor) {
  if (!amount) return;
  const number = document.createElement('span');
  number.className = `combat-number ${targetActor === 'enemy' ? 'enemy-hit' : 'player-hit'}`;
  number.textContent = `-${formatNumber(amount)}`;
  const activeNumbers = battlefield.querySelectorAll(`.combat-number.${targetActor === 'enemy' ? 'enemy-hit' : 'player-hit'}`).length;
  number.style.top = `${39 + Math.min(activeNumbers, 2) * 5}%`;
  number.style.setProperty('--drift', `${(Math.random() - .5) * 42}px`);
  battlefield.append(number);
  number.addEventListener('animationend', () => number.remove(), { once: true });
}

function playAnimation(actor, type, onImpact = null) {
  return new Promise((resolve) => {
    const durations = { attack: 820, rasengan: 1380, clones: 1240, wind: 1120, sand: 1220, guard: 880, recharge: 1080 };
    actionAnimation = {
      actor,
      type,
      start: performance.now(),
      duration: (durations[type] || 950) * (state.settings.reducedEffects ? .58 : 1),
      impactTriggered: false,
      cloneHitStage: 0,
      clonesSpawned: false,
      sparkTimer: 0,
      onImpact,
      resolve,
    };
    playRiggedAnimation(actor === 'player' ? playerModel : enemyModel, type, .12, false);
  });
}

function triggerAnimationImpact() {
  if (!actionAnimation?.onImpact) return;
  const targetActor = actionAnimation.actor === 'player' ? 'enemy' : 'player';
  playRiggedAnimation(targetActor === 'enemy' ? enemyModel : playerModel, 'hit', .06, false);
  const damage = actionAnimation.onImpact();
  showCombatNumber(damage, targetActor);
}

function applyDamage(target, amount, pierce = false) {
  let actual = amount;
  if (target.guard) {
    actual = Math.round(amount * (pierce ? .72 : .43));
    target.guard = false;
  }
  target.hp = Math.max(0, target.hp - actual);
  return actual;
}

function applyTechniqueEffect(action, damage) {
  if (!action.status || Math.random() > .44) return '';
  if (action.status === 'burn') {
    state.enemy.status = { type: 'burn', turns: 2, power: Math.max(12, Math.round(damage * .09)) };
    return ' • queimadura aplicada';
  }
  if (action.status === 'exposed') {
    state.enemy.status = { type: 'exposed', turns: 1 };
    state.enemy.guard = false;
    return ' • defesa exposta';
  }
  if (action.status === 'drain') {
    const drained = Math.min(55, state.enemy.chakra);
    state.enemy.chakra -= drained;
    state.player.chakra = Math.min(state.player.maxChakra, state.player.chakra + Math.round(drained * .55));
    return ` • drenou ${drained} de energia`;
  }
  if (action.status === 'weakened') {
    state.enemy.status = { type: 'weakened', turns: 2 };
    return ' • ataque inimigo enfraquecido';
  }
  if (action.status === 'charged') return ' • sobrecarga elétrica';
  if (action.status === 'leech') {
    const healed = Math.round(damage * .22);
    state.player.hp = Math.min(state.player.maxHp, state.player.hp + healed);
    return ` • recuperou ${healed} de vida`;
  }
  return '';
}

function enterBattle(enemy) {
  if (state.mode !== 'world' || enemy.defeated || encounterCooldown > 0) return;
  currentEnemy = enemy;
  state.mode = 'battle';
  state.turn = 1;
  state.round = 1;
  state.phase = 'player';
  state.selected = 'attack';
  state.locked = false;
  state.gameOver = false;
  state.player.guard = false;
  state.player.chakra = Math.min(state.player.maxChakra, state.player.chakra + 75);
  const enemyHp = Math.round((920 + enemy.level * 150) * (enemy.hp || 1));
  state.enemy = { hp: enemyHp, maxHp: enemyHp, chakra: 420 + enemy.level * 18, maxChakra: 600, guard: false, affinity: enemy.affinity, status: null };
  state.player.status = null;
  const enemyAffinity = AFFINITIES[enemy.affinity] || AFFINITIES.stone;
  enemyModel.userData.materials.accentMat.color.setHex(enemyAffinity.hex);
  enemyModel.userData.materials.accentMat.emissive?.setHex(enemyAffinity.hex);
  enemyModel.userData.parts.aura.material.color.setHex(enemyAffinity.hex);
  attachRiggedModel(enemyModel, { ...enemy.modelSpec, position: [-.32, 0, 0], rotationY: -Math.PI / 2 }).catch((error) => {
    console.warn('Modelo detalhado do inimigo indisponível; mantendo o fallback procedural.', error);
  });
  $('#enemyName').textContent = enemy.name;
  $('#enemyLevel').textContent = `Nv. ${enemy.level}`;
  $('#app').classList.remove('world-mode');
  world.visible = true;
  mapWorld.visible = false;
  cameraAngle = 0;
  cameraLift = 0;
  mapTarget = null;
  mapVelocity.set(0, 0, 0);
  log(`encontrou ${enemy.name}. Prepare-se para o combate!`);
  tone(180, .26, 'sawtooth');
  updateUI();
  requestAnimationFrame(resize);
}

async function executePlayerAction() {
  if (state.locked || state.phase !== 'player' || state.gameOver) return;
  const action = actions[state.selected];
  if ((action.mastery || 0) > currentMastery().level) {
    log(`precisa alcançar domínio ${action.mastery} de ${currentAffinity().name}.`);
    return;
  }
  if (action.cost > state.player.chakra) {
    log('não tem energia suficiente para esta técnica.');
    tone(90, .14, 'square');
    return;
  }

  state.locked = true;
  state.player.chakra -= action.cost;
  updateUI();
  log(`prepara ${action.label}.`);
  tone(action.anim === 'rasengan' ? 420 : 230, .16, 'sawtooth');
  let attackResult = null;
  let impactHandler = null;
  if (action.min) {
    const elementScale = elementMultiplier(action.element, state.enemy.affinity, AFFINITIES);
    const criticalChance = .06 + state.stats.spd * .008 + (state.affinity === 'storm' ? .14 : 0);
    const critical = Math.random() < criticalChance;
    const calculated = calculateDamage({
      base: randomBetween(action.min, action.max), level: state.level, attackPoints: state.stats.atk,
      attackBonus: combinedBonuses().atk, rank: action.rank, element: elementScale, critical,
    });
    const exposedScale = state.enemy.status?.type === 'exposed' ? 1.15 : 1;
    if (state.enemy.status?.type === 'exposed') state.enemy.status = null;
    const plannedTotal = Math.round(calculated * exposedScale);
    const hitCount = action.hits || 1;
    let hitsApplied = 0;
    let dealt = 0;
    attackResult = { elementScale, critical, get damage() { return dealt; } };
    impactHandler = () => {
      const remaining = plannedTotal - dealt;
      const plannedHit = hitsApplied === hitCount - 1 ? remaining : Math.max(1, Math.round(plannedTotal / hitCount));
      hitsApplied += 1;
      const actual = applyDamage(state.enemy, plannedHit, action.pierce);
      dealt += actual;
      updateUI();
      return actual;
    };
  }
  await playAnimation('player', action.anim, impactHandler);

  if (state.selected === 'guard') {
    state.player.guard = true;
    state.player.chakra = Math.min(state.player.maxChakra, state.player.chakra + 15);
    log('assume uma postura defensiva. Dano recebido será reduzido.');
  } else if (state.selected === 'recharge') {
    state.player.chakra = Math.min(state.player.maxChakra, state.player.chakra + 145);
    log(`recuperou <strong>145</strong> de chakra.`);
  } else {
    const effectMessage = applyTechniqueEffect(action, attackResult.damage);
    const advantage = attackResult.elementScale > 1 ? ' • <em>VANTAGEM ELEMENTAL</em>' : attackResult.elementScale < 1 ? ' • resistência elemental' : '';
    log(`causou <strong>${attackResult.damage}</strong> de dano com ${action.label}${attackResult.critical ? ' • <em>CRÍTICO</em>' : ''}${advantage}${effectMessage}.`);
  }

  updateUI();
  if (checkGameOver()) return;
  state.phase = 'enemy';
  state.turn += 1;
  state.locked = true;
  updateUI();
  await wait(700);
  executeEnemyAction();
}

async function executeEnemyAction() {
  if (state.gameOver) return;
  if (state.enemy.status?.type === 'burn') {
    const burnDamage = Math.min(state.enemy.hp, state.enemy.status.power);
    state.enemy.hp -= burnDamage;
    state.enemy.status.turns -= 1;
    log(`sofreu <strong>${burnDamage}</strong> de dano da queimadura.`, currentEnemy?.name || 'INIMIGO');
    if (state.enemy.status.turns <= 0) state.enemy.status = null;
    updateUI();
    if (checkGameOver()) return;
    await wait(360);
  }
  let choice;
  const behavior = currentEnemy?.behavior || 'aggressive';
  const guardChance = behavior === 'defensive' ? .62 : behavior === 'technical' ? .3 : .2;
  const techniqueChance = behavior === 'technical' ? .76 : behavior === 'aggressive' ? .64 : .43;
  if (state.enemy.hp < state.enemy.maxHp * .42 && !state.enemy.guard && Math.random() < guardChance) choice = 'guard';
  else if (state.enemy.chakra < 70) choice = 'recharge';
  else if (state.enemy.chakra >= 70 && Math.random() < techniqueChance) choice = 'sand';
  else choice = 'attack';

  const enemyAction = {
    attack: { label: 'Golpe do Errante', cost: 0, min: 165, max: 210, anim: 'attack', element: null },
    sand: { label: AFFINITIES[state.enemy.affinity].skill.label, cost: 70, min: 245, max: 292, anim: 'sand', pierce: state.enemy.affinity === 'stone', element: state.enemy.affinity },
    guard: { label: 'Muralha de Energia', cost: 0, anim: 'guard' },
    recharge: { label: 'Concentrar Energia', cost: 0, anim: 'recharge' },
  }[choice];

  state.enemy.chakra -= enemyAction.cost;
  log(`prepara ${enemyAction.label}.`, 'REN');
  updateUI();
  tone(choice === 'sand' ? 140 : 190, .16, 'triangle');
  let enemyDamage = 0;
  let enemyImpactHandler = null;
  if (enemyAction.min) {
    const defenseReduction = Math.min(.58, state.stats.def * .04 + combinedBonuses().def);
    const enemyMultiplier = (1 + Math.max(0, (currentEnemy?.level || 1) - 1) * .055) * (currentEnemy?.attack || 1) * (1 - defenseReduction);
    const elemental = elementMultiplier(enemyAction.element, state.affinity, AFFINITIES);
    const weakened = state.enemy.status?.type === 'weakened' ? .82 : 1;
    const plannedDamage = Math.round(randomBetween(enemyAction.min, enemyAction.max) * enemyMultiplier * elemental * weakened);
    enemyImpactHandler = () => {
      enemyDamage = applyDamage(state.player, plannedDamage, enemyAction.pierce);
      updateUI();
      return enemyDamage;
    };
  }
  await playAnimation('enemy', enemyAction.anim, enemyImpactHandler);

  if (choice === 'guard') {
    state.enemy.guard = true;
    state.enemy.chakra = Math.min(state.enemy.maxChakra, state.enemy.chakra + 10);
    log('ergueu uma muralha defensiva.', 'REN');
  } else if (choice === 'recharge') {
    state.enemy.chakra = Math.min(state.enemy.maxChakra, state.enemy.chakra + 135);
    log('recuperou chakra.', 'REN');
  } else {
    log(`causou <strong>${enemyDamage}</strong> de dano com ${enemyAction.label}.`, 'REN');
    if (state.enemy.status?.type === 'weakened') {
      state.enemy.status.turns -= 1;
      if (state.enemy.status.turns <= 0) state.enemy.status = null;
    }
  }

  updateUI();
  if (checkGameOver()) return;
  state.phase = 'player';
  state.turn += 1;
  state.round += 1;
  state.locked = false;
  state.player.chakra = Math.min(state.player.maxChakra, state.player.chakra + 12);
  state.enemy.chakra = Math.min(state.enemy.maxChakra, state.enemy.chakra + 10);
  log('está aguardando seu próximo comando.');
  updateUI();
}

function checkGameOver() {
  if (state.player.hp > 0 && state.enemy.hp > 0) return false;
  state.gameOver = true;
  state.locked = true;
  const victory = state.enemy.hp <= 0;
  let xpReward = 0;
  let coinReward = 0;
  let pointReward = 0;
  let masteryReward = 0;
  let powerReward = 0;
  let achievementsUnlocked = [];
  let leveledUp = false;
  if (victory && currentEnemy) {
    currentEnemy.defeated = true;
    currentEnemy.model.visible = false;
    const bossScale = currentEnemy.boss ? 2.4 : 1;
    xpReward = Math.round((45 + currentEnemy.level * 38) * bossScale);
    coinReward = Math.round((55 + currentEnemy.level * 32) * bossScale);
    pointReward = 1;
    state.xp += xpReward;
    state.coins += coinReward;
    state.records.victories += 1;
    state.records.coinsEarned += coinReward;
    if (currentEnemy.boss) state.records.bossesDefeated += 1;
    state.statPoints += pointReward;
    const levelProgress = resolveProgress(state.xp, state.level, xpNeeded, 3);
    state.xp = levelProgress.value;
    state.level = levelProgress.level;
    leveledUp = levelProgress.levelsGained > 0;
    state.statPoints += levelProgress.points;
    pointReward += levelProgress.points;
    masteryReward = Math.round((34 + currentEnemy.level * 14) * (1 + combinedBonuses().mastery));
    const mastery = currentMastery();
    const masteryProgress = resolveProgress(mastery.xp + masteryReward, mastery.level, masteryNeeded, 1);
    mastery.xp = masteryProgress.value;
    mastery.level = masteryProgress.level;
    powerReward = masteryProgress.points;
    state.powerPoints += powerReward;
    achievementsUnlocked = unlockAchievements();
    recalculatePlayerCaps(true);
    state.player.hp = Math.min(state.player.maxHp, state.player.hp + Math.round(state.player.maxHp * .34));
    state.player.chakra = Math.min(state.player.maxChakra, state.player.chakra + 100);
    saveProgress();
  }
  $('#resultTitle').textContent = victory ? 'VITÓRIA' : 'DERROTA';
  $('#resultTitle').style.color = victory ? '#7af2fc' : '#ff8b68';
  $('#resultSubtitle').textContent = victory
    ? `+${xpReward} XP • +${coinReward} RYO • +${masteryReward} DOMÍNIO • +${pointReward} TREINO${powerReward ? ` • +${powerReward} PODER` : ''}${leveledUp ? ` • NÍVEL ${state.level}!` : ''}${achievementsUnlocked.length ? ` • CONQUISTA: ${achievementsUnlocked.map((item) => item.label).join(', ')}` : ''}`
    : 'Você foi resgatado e poderá tentar novamente.';
  $('#restartBtn').textContent = victory ? 'VOLTAR AO MAPA' : 'RECUPERAR E VOLTAR';
  $('#resultOverlay').classList.add('show');
  $('#resultOverlay').setAttribute('aria-hidden', 'false');
  tone(victory ? 520 : 110, .6, victory ? 'sine' : 'sawtooth');
  updateUI();
  return true;
}

function resetGame() {
  const wasDefeated = state.player.hp <= 0;
  state.mode = 'world';
  state.phase = 'player';
  state.selected = 'attack';
  state.locked = false;
  state.gameOver = false;
  state.player.guard = false;
  if (wasDefeated) {
    state.player.hp = state.player.maxHp;
    state.player.chakra = state.player.maxChakra;
    mapPlayer.position.set(0, 0, 8.2);
  }
  $('#resultOverlay').classList.remove('show');
  $('#resultOverlay').setAttribute('aria-hidden', 'true');
  $('#app').classList.add('world-mode');
  world.visible = false;
  mapWorld.visible = true;
  encounterCooldown = 1.4;
  currentEnemy = null;
  if (mapEnemies.every((enemy) => enemy.defeated)) {
    enemyWave += 1;
    spawnWorldEnemies(state.level + enemyWave - 1);
    $('#encounterTip').textContent = `NOVA ONDA: INIMIGOS MAIS FORTES CHEGARAM`;
  } else {
    $('#encounterTip').textContent = 'APROXIME-SE DE UM INIMIGO PARA LUTAR';
  }
  updateUI();
  requestAnimationFrame(resize);
}

function selectAction(name) {
  if (state.locked || state.phase !== 'player' || state.gameOver) return;
  const action = actions[name];
  if ((action.mastery || 0) > currentMastery().level) {
    log(`precisa de domínio ${action.mastery} de ${currentAffinity().name} para aprender esta técnica.`);
    tone(90, .11, 'square');
    return;
  }
  if (action.cost > state.player.chakra) {
    log('não tem energia suficiente para essa técnica.');
    tone(90, .11, 'square');
    return;
  }
  state.selected = name;
  log(`selecionou ${action.label}. Confirme para agir.`);
  tone(330, .055);
  updateUI();
}

$$('[data-action]').forEach((button) => button.addEventListener('click', () => selectAction(button.dataset.action)));
$('#confirmBtn').addEventListener('click', executePlayerAction);
$('#restartBtn').addEventListener('click', resetGame);
window.addEventListener('keydown', (event) => {
  if (event.code === 'Escape' && $('#progressionOverlay').classList.contains('show')) {
    closeProgressionMenu();
    return;
  }
  const movementKeys = {
    KeyW: 'up', ArrowUp: 'up',
    KeyS: 'down', ArrowDown: 'down',
    KeyA: 'left', ArrowLeft: 'left',
    KeyD: 'right', ArrowRight: 'right',
  };
  if (state.mode === 'world' && movementKeys[event.code]) {
    event.preventDefault();
    moveInput[movementKeys[event.code]] = true;
    mapTarget = null;
    return;
  }
  if (state.mode === 'world' && ['Equal', 'NumpadAdd', 'Minus', 'NumpadSubtract'].includes(event.code)) {
    event.preventDefault();
    const zoomIn = event.code === 'Equal' || event.code === 'NumpadAdd';
    setMapZoom(targetMapZoom + (zoomIn ? -.12 : .12));
    return;
  }
  if (event.repeat || state.mode !== 'battle') return;
  const keyMap = { Digit1: 'attack', Digit2: 'rasengan', Digit3: 'clones', Digit4: 'wind', KeyA: 'guard', KeyS: 'recharge' };
  if (keyMap[event.code]) selectAction(keyMap[event.code]);
  if (event.code === 'Space') {
    event.preventDefault();
    executePlayerAction();
  }
});
window.addEventListener('keyup', (event) => {
  const movementKeys = {
    KeyW: 'up', ArrowUp: 'up',
    KeyS: 'down', ArrowDown: 'down',
    KeyA: 'left', ArrowLeft: 'left',
    KeyD: 'right', ArrowRight: 'right',
  };
  if (movementKeys[event.code]) moveInput[movementKeys[event.code]] = false;
});
window.addEventListener('blur', () => Object.keys(moveInput).forEach((key) => { moveInput[key] = false; }));

$$('[data-move]').forEach((button) => {
  const direction = button.dataset.move;
  const start = (event) => {
    event.preventDefault();
    moveInput[direction] = true;
    mapTarget = null;
    button.setPointerCapture?.(event.pointerId);
  };
  const stop = (event) => {
    event.preventDefault();
    moveInput[direction] = false;
  };
  button.addEventListener('pointerdown', start);
  button.addEventListener('pointerup', stop);
  button.addEventListener('pointercancel', stop);
  button.addEventListener('pointerleave', stop);
});

function wait(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

const timer = new THREE.Timer();
timer.connect(document);
function animate(timestamp) {
  requestAnimationFrame(animate);
  if (document.hidden) return;
  timer.update(timestamp);
  const delta = Math.min(timer.getDelta(), .05);
  const elapsed = timer.getElapsed();
  [playerModel, enemyModel].forEach((ninja, index) => {
    const offset = index * 1.7;
    const breath = Math.sin(elapsed * 2.1 + offset);
    const stanceSide = index ? -1 : 1;
    ninja.userData.model.position.y = breath * .018;
    ninja.userData.model.rotation.x = .025 + breath * .008;
    ninja.userData.parts.head.rotation.z = Math.sin(elapsed * 1.1 + offset) * .018;
    ninja.userData.parts.leftArm.rotation.z = stanceSide * (-.09 + breath * .025);
    ninja.userData.parts.rightArm.rotation.z = stanceSide * (.13 - breath * .025);
    ninja.userData.parts.leftLeg.rotation.z = stanceSide * -.055;
    ninja.userData.parts.rightLeg.rotation.z = stanceSide * .07;
    ninja.userData.parts.aura.material.opacity = .11 + Math.sin(elapsed * 3 + offset) * .04;
    ninja.userData.parts.aura.scale.setScalar(1 + Math.sin(elapsed * 2.5 + offset) * .05);
  });
  [playerModel, enemyModel, mapPlayer, ...shadowClones].forEach((model) => updateRiggedModel(model, delta));
  if (enemyModel.userData.sandOrb) enemyModel.userData.sandOrb.rotation.y += .004;
  ring.material.opacity = .12 + Math.sin(elapsed * .9) * .04;

  if (state.mode === 'world') updateWorldMovement(delta, elapsed);
  if (actionAnimation) updateActionAnimation(performance.now());
  updateParticles(delta);
  camera.position.sub(previousShake);
  previousShake.set(0, 0, 0);
  if (state.mode === 'battle' && cameraShake > .001) {
    previousShake.set(
      (Math.random() - .5) * cameraShake,
      (Math.random() - .5) * cameraShake * .65,
      (Math.random() - .5) * cameraShake * .35
    );
    camera.position.add(previousShake);
    cameraShake *= Math.pow(.035, delta);
  }
  renderer.render(scene, camera);
}

function updateWorldMovement(delta, elapsed) {
  encounterCooldown = Math.max(0, encounterCooldown - delta);
  mapZoom = THREE.MathUtils.lerp(mapZoom, targetMapZoom, 1 - Math.exp(-7 * delta));
  const direction = new THREE.Vector3(
    Number(moveInput.right) - Number(moveInput.left),
    0,
    Number(moveInput.down) - Number(moveInput.up)
  );

  if (direction.lengthSq() === 0 && mapTarget) {
    direction.subVectors(mapTarget, mapPlayer.position);
    direction.y = 0;
    if (direction.length() < .16) {
      mapTarget = null;
      direction.set(0, 0, 0);
    }
  }

  const wantsToMove = direction.lengthSq() > .001;
  const movementSpeed = 3.45 * (1 + state.stats.spd * .035) * (1 + combinedBonuses().spd);
  const desiredVelocity = wantsToMove ? direction.normalize().multiplyScalar(movementSpeed) : new THREE.Vector3();
  const acceleration = wantsToMove ? 9.5 : 6.8;
  mapVelocity.lerp(desiredVelocity, 1 - Math.exp(-acceleration * delta));
  const currentSpeed = mapVelocity.length();
  const walking = currentSpeed > .08;
  playRiggedAnimation(mapPlayer, walking ? 'walk' : 'idle', .18, true);
  if (walking) {
    const travelDirection = mapVelocity.clone().normalize();
    direction.normalize();
    const nextX = THREE.MathUtils.clamp(mapPlayer.position.x + mapVelocity.x * delta, -13.3, 13.3);
    const nextZ = THREE.MathUtils.clamp(mapPlayer.position.z + mapVelocity.z * delta, -10.8, 10.8);
    if (canWalkTo(nextX, nextZ)) {
      mapPlayer.position.x = nextX;
      mapPlayer.position.z = nextZ;
    } else if (canWalkTo(nextX, mapPlayer.position.z)) {
      mapPlayer.position.x = nextX;
      mapVelocity.z *= .35;
    } else if (canWalkTo(mapPlayer.position.x, nextZ)) {
      mapPlayer.position.z = nextZ;
      mapVelocity.x *= .35;
    } else {
      mapVelocity.multiplyScalar(.3);
      mapTarget = null;
    }
    const desiredRotation = Math.atan2(travelDirection.x, travelDirection.z);
    let turnDifference = desiredRotation - mapPlayer.rotation.y;
    turnDifference = Math.atan2(Math.sin(turnDifference), Math.cos(turnDifference));
    mapPlayer.rotation.y += turnDifference * Math.min(1, delta * 9);
    const moveBlend = THREE.MathUtils.clamp(currentSpeed / movementSpeed, 0, 1);
    walkPhase += delta * (7.5 + currentSpeed * 1.3);
    const stride = Math.sin(walkPhase);
    const bounce = Math.abs(Math.sin(walkPhase));
    const parts = mapPlayer.userData.parts;
    parts.leftLeg.rotation.x = stride * .58 * moveBlend;
    parts.rightLeg.rotation.x = -stride * .58 * moveBlend;
    parts.leftLeg.rotation.z = -.04 * moveBlend;
    parts.rightLeg.rotation.z = .04 * moveBlend;
    parts.leftArm.rotation.x = -stride * .42 * moveBlend;
    parts.rightArm.rotation.x = stride * .42 * moveBlend;
    parts.leftArm.rotation.z = -.08 * moveBlend;
    parts.rightArm.rotation.z = .08 * moveBlend;
    parts.head.rotation.x = -.045 * moveBlend;
    parts.head.rotation.z = -turnDifference * .2;
    mapPlayer.userData.model.position.y = bounce * .055 * moveBlend;
    mapPlayer.userData.model.rotation.x = .085 * moveBlend;
    mapPlayer.userData.model.rotation.z = -turnDifference * .12;
  } else {
    const parts = mapPlayer.userData.parts;
    parts.leftLeg.rotation.x *= .78;
    parts.rightLeg.rotation.x *= .78;
    parts.leftLeg.rotation.z *= .78;
    parts.rightLeg.rotation.z *= .78;
    parts.leftArm.rotation.x *= .78;
    parts.rightArm.rotation.x *= .78;
    parts.leftArm.rotation.z *= .78;
    parts.rightArm.rotation.z *= .78;
    parts.head.rotation.x *= .8;
    parts.head.rotation.z *= .8;
    mapPlayer.userData.model.position.y = Math.sin(elapsed * 2.4) * .015;
    mapPlayer.userData.model.rotation.x *= .8;
    mapPlayer.userData.model.rotation.z *= .8;
  }

  let nearestDistance = Infinity;
  let nearestEnemy = null;
  mapEnemies.forEach((enemy, index) => {
    if (enemy.defeated || !enemy.model.visible) return;
    const patrolSpeed = .34 + index * .025;
    const nextPatrolX = enemy.originX + Math.sin(elapsed * patrolSpeed + enemy.patrolPhase) * .48;
    const nextPatrolZ = enemy.originZ + Math.cos(elapsed * patrolSpeed * .78 + enemy.patrolPhase) * .36;
    const patrolHeading = Math.atan2(nextPatrolX - enemy.model.position.x, nextPatrolZ - enemy.model.position.z);
    enemy.model.position.x = THREE.MathUtils.lerp(enemy.model.position.x, nextPatrolX, delta * 1.2);
    enemy.model.position.z = THREE.MathUtils.lerp(enemy.model.position.z, nextPatrolZ, delta * 1.2);
    enemy.model.rotation.y += Math.atan2(Math.sin(patrolHeading - enemy.model.rotation.y), Math.cos(patrolHeading - enemy.model.rotation.y)) * Math.min(1, delta * 2.2);
    enemy.model.userData.alertRing.rotation.z += delta * (1 + index * .08);
    enemy.model.userData.alertRing.material.opacity = .45 + Math.sin(elapsed * 3 + index) * .17;
    enemy.model.userData.alertRing.scale.setScalar(1 + Math.sin(elapsed * 2.5 + index) * .08);
    enemy.model.userData.model.position.y = Math.sin(elapsed * 2 + index) * .025;
    const dx = mapPlayer.position.x - enemy.model.position.x;
    const dz = mapPlayer.position.z - enemy.model.position.z;
    const distance = Math.hypot(dx, dz);
    if (distance < nearestDistance) {
      nearestDistance = distance;
      nearestEnemy = enemy;
    }
  });

  if (nearestEnemy && nearestDistance < 2.7) {
    $('#encounterTip').textContent = `${nearestEnemy.name} • NV. ${nearestEnemy.level}`;
  } else if (mapEnemies.some((enemy) => !enemy.defeated)) {
    $('#encounterTip').textContent = 'APROXIME-SE DE UM INIMIGO PARA LUTAR';
  }
  if (nearestEnemy && nearestDistance < 1.05 && encounterCooldown <= 0) enterBattle(nearestEnemy);

  const desiredCamera = mapPlayer.position.clone().add(mapCameraOffset());
  camera.position.lerp(desiredCamera, 1 - Math.pow(.001, delta));
  camera.lookAt(mapPlayer.position.x, .6, mapPlayer.position.z - 1.2 * mapZoom);
}

function updateActionAnimation(now) {
  const { actor, type, start, duration } = actionAnimation;
  const p = Math.min(1, (now - start) / duration);
  const source = actor === 'player' ? playerModel : enemyModel;
  const target = actor === 'player' ? enemyModel : playerModel;
  const side = actor === 'player' ? 1 : -1;
  const parts = source.userData.parts;
  const targetParts = target.userData.parts;
  const techniqueAffinity = actor === 'player' ? currentAffinity() : AFFINITIES[state.enemy.affinity] || AFFINITIES.stone;
  const techniqueColor = techniqueAffinity.hex;
  const smooth = (value) => {
    const clamped = THREE.MathUtils.clamp(value, 0, 1);
    return clamped * clamped * (3 - 2 * clamped);
  };
  const anticipation = Math.sin(Math.min(1, p / .24) * Math.PI * .5);
  const strike = smooth((p - .2) / .3);
  const recovery = smooth((p - .68) / .32);
  const lunge = strike * (1 - recovery);
  const damaging = ['attack', 'rasengan', 'clones', 'wind', 'sand'].includes(type);
  const impactPoint = type === 'wind' ? .64 : type === 'sand' ? .62 : type === 'rasengan' ? .57 : type === 'clones' ? .66 : .5;

  if (damaging) {
    parts.head.rotation.z = side * (.08 * anticipation - .1 * lunge);
    source.rotation.z = side * (-.055 * lunge + .025 * anticipation);
    parts.leftLeg.rotation.x = .22 * anticipation - .42 * lunge;
    parts.rightLeg.rotation.x = -.28 * anticipation + .34 * lunge;
    source.userData.model.rotation.y = side * (.1 * anticipation - .2 * lunge);
    source.userData.model.rotation.x = .06 * anticipation - .09 * lunge;
  }

  if (type === 'attack') {
    source.position.x = source.userData.baseX - side * .16 * anticipation + side * 1.55 * lunge;
    source.position.y = -.09 * anticipation + .08 * lunge;
    parts.rightArm.rotation.z = side * (.72 * anticipation - 1.55 * lunge);
    parts.rightArm.rotation.x = -.6 * anticipation + 1.2 * lunge;
    parts.leftArm.rotation.z = side * (-.35 * anticipation - .38 * lunge);
    parts.leftArm.rotation.x = .42 * anticipation - .35 * lunge;
    parts.rightLeg.rotation.z = side * -.24 * lunge;
    slashArc.visible = p > .31 && p < .59;
    slashArc.position.set(source.position.x + side * .62, 1.36, .25);
    slashArc.rotation.z = side > 0 ? -.82 + strike * .85 : Math.PI + .82 - strike * .85;
    slashArc.scale.setScalar(.65 + strike * .7);
    slashArc.material.opacity = Math.max(0, 1 - Math.abs(p - .43) * 5);
  }

  if (type === 'rasengan') {
    const charge = smooth(p / .38);
    const orbStrike = smooth((p - .38) / .24);
    const orbRecovery = smooth((p - .7) / .3);
    const orbLunge = orbStrike * (1 - orbRecovery);
    source.position.x = source.userData.baseX - side * .18 * charge + side * 1.62 * orbLunge;
    source.position.y = -.1 * charge + .1 * orbLunge;
    parts.rightArm.rotation.z = side * (-.42 - 1.0 * orbLunge);
    parts.leftArm.rotation.z = side * (.55 * charge - .7 * orbLunge);
    parts.leftArm.rotation.x = -.55 * charge;
    parts.rightArm.rotation.x = .35 * charge - 1.05 * orbLunge;
    source.userData.model.rotation.y = side * (-.22 * charge + .4 * orbLunge);
    energyOrb.visible = true;
    energyOrb.material.color.setHex(techniqueColor);
    energyOrb.material.emissive.setHex(techniqueColor);
    energyOrb.position.set(source.position.x + side * .58, 1.33, .25);
    energyOrb.scale.setScalar(.12 + charge * .92 + orbLunge * .35);
    energyOrb.rotation.x += .22;
    energyOrb.rotation.y += .28;
    if (p < .55 && now - actionAnimation.sparkTimer > 95) {
      burstParticles(energyOrb.position, techniqueColor, 5, .26);
      actionAnimation.sparkTimer = now;
    }
  }

  if (type === 'wind') {
    const cast = smooth(p / .32);
    const projectile = smooth((p - .28) / .4);
    source.position.x = source.userData.baseX - side * .12 * cast;
    source.rotation.y = side * -.18 * Math.sin(Math.min(1, p / .45) * Math.PI);
    parts.rightArm.rotation.z = side * (.85 * cast - 1.2 * projectile);
    parts.leftArm.rotation.z = side * -.7 * cast;
    parts.rightArm.rotation.x = -.55 * cast + .85 * projectile;
    parts.leftArm.rotation.x = .35 * cast - .25 * projectile;
    windRings.forEach((wind, index) => {
      wind.visible = true;
      wind.material.color.setHex(techniqueColor);
      const stagger = THREE.MathUtils.clamp(projectile - index * .07, 0, 1);
      wind.position.set(
        THREE.MathUtils.lerp(source.userData.baseX + side * .58, target.userData.baseX, stagger),
        1.25 + Math.sin(index * 1.8 + now * .01) * .13,
        .12 + Math.sin(index) * .08
      );
      wind.rotation.x += .18;
      wind.rotation.z += side * .12;
      wind.scale.setScalar(.42 + stagger * 1.25);
      wind.material.opacity = Math.max(0, .86 - Math.max(0, projectile - .85) * 5);
    });
  }

  if (type === 'clones') {
    const summon = smooth(p / .28);
    if (!actionAnimation.clonesSpawned && p > .08) {
      actionAnimation.clonesSpawned = true;
      burstParticles(new THREE.Vector3(source.userData.baseX - side * .45, .45, .7), 0xd9fbff, 18, .55);
      burstParticles(new THREE.Vector3(source.userData.baseX - side * .62, .45, -.7), 0xd9fbff, 18, .55);
      tone(260, .12, 'sine');
      shadowClones.forEach((clone) => playRiggedAnimation(clone, 'attack', .05, false));
    }
    const heroStrike = smooth((p - .43) / .26);
    const heroRecovery = smooth((p - .76) / .22);
    const heroLunge = heroStrike * (1 - heroRecovery);
    source.position.x = source.userData.baseX + side * 1.32 * heroLunge;
    source.position.z = Math.sin(p * Math.PI * 2) * .09;
    source.position.y = -.07 * summon + .11 * heroLunge;
    parts.rightArm.rotation.z = side * (-.35 - 1.2 * heroLunge);
    parts.leftArm.rotation.z = side * (.45 - .72 * heroLunge);
    parts.leftLeg.rotation.z = side * -.18 * heroLunge;
    parts.rightLeg.rotation.z = side * .18 * heroLunge;

    shadowClones.forEach((clone, index) => {
      const lane = index ? -.72 : .72;
      const startMoment = .22 + index * .1;
      const cloneStrike = smooth((p - startMoment) / .25);
      const cloneRecovery = smooth((p - (.63 + index * .08)) / .24);
      const cloneLunge = cloneStrike * (1 - cloneRecovery);
      const cloneParts = clone.userData.parts;
      clone.visible = p > .07 && p < .92;
      clone.position.set(
        THREE.MathUtils.lerp(source.userData.baseX - side * (.45 + index * .18), target.userData.baseX - side * (.34 + index * .12), cloneLunge),
        Math.sin(cloneStrike * Math.PI) * (.2 + index * .08),
        lane * (summon - cloneStrike * .56)
      );
      clone.rotation.y = actor === 'player' ? .08 : -.08;
      clone.rotation.z = side * -.16 * cloneLunge;
      cloneParts.rightArm.rotation.z = side * (.52 * summon - 1.62 * cloneLunge);
      cloneParts.leftArm.rotation.z = side * (-.28 * summon - .5 * cloneLunge);
      cloneParts.rightLeg.rotation.z = side * .24 * cloneLunge;
      cloneParts.leftLeg.rotation.z = side * -.2 * cloneLunge;
      cloneParts.head.rotation.z = side * -.12 * cloneLunge;
      cloneParts.aura.material.opacity = .18 + summon * .2;

      const arc = cloneSlashArcs[index];
      const arcStart = startMoment + .12;
      arc.visible = p > arcStart && p < arcStart + .2;
      arc.position.set(clone.position.x + side * .52, 1.25 + clone.position.y, clone.position.z);
      arc.rotation.z = side > 0 ? -.72 + cloneStrike * .62 : Math.PI + .72 - cloneStrike * .62;
      arc.scale.setScalar(.55 + cloneStrike * .75);
      arc.material.opacity = Math.max(0, 1 - Math.abs(p - (arcStart + .08)) * 9);
    });

    const cloneHitMoments = [.43, .54];
    while (actionAnimation.cloneHitStage < cloneHitMoments.length && p >= cloneHitMoments[actionAnimation.cloneHitStage]) {
      const hitIndex = actionAnimation.cloneHitStage;
      const hitPosition = new THREE.Vector3(target.userData.baseX - side * .28, 1.25, hitIndex ? -.24 : .24);
      burstParticles(hitPosition, hitIndex ? 0x69d7ff : 0x83f5ff, 15, .72);
      cameraShake = Math.max(cameraShake, .1);
      tone(132 + hitIndex * 30, .1, 'triangle');
      triggerAnimationImpact();
      actionAnimation.cloneHitStage += 1;
    }

    slashArc.visible = p > .57 && p < .76;
    slashArc.position.set(source.position.x + side * .62, 1.28, .18);
    slashArc.rotation.z = side > 0 ? -.55 : Math.PI + .55;
    slashArc.scale.setScalar(.75 + heroStrike * .68);
    slashArc.material.opacity = Math.max(0, 1 - Math.abs(p - .65) * 7);
  }

  if (type === 'sand') {
    const gather = smooth(p / .35);
    const projectile = smooth((p - .3) / .36);
    source.position.x = source.userData.baseX - side * .12 * gather;
    parts.rightArm.rotation.z = side * (-.3 - .9 * gather);
    parts.leftArm.rotation.z = side * (.25 - .5 * gather);
    energyOrb.visible = true;
    energyOrb.material.color.setHex(techniqueColor);
    energyOrb.material.emissive.setHex(techniqueColor);
    energyOrb.position.set(
      THREE.MathUtils.lerp(source.userData.baseX + side * .62, target.userData.baseX, projectile),
      .7 + gather * .75 + Math.sin(projectile * Math.PI) * .38,
      .18
    );
    energyOrb.scale.set(.28 + gather * .65, .18 + gather * .3, .28 + gather * .65);
    energyOrb.rotation.x += .16;
    energyOrb.rotation.y += .2;
    if (now - actionAnimation.sparkTimer > 105 && p < .67) {
      burstParticles(energyOrb.position, techniqueColor, 4, .2);
      actionAnimation.sparkTimer = now;
    }
  }

  if (type === 'guard') {
    const guardPower = Math.sin(p * Math.PI);
    source.scale.set(1 + guardPower * .04, 1 - guardPower * .04, 1 + guardPower * .04);
    source.position.y = -.08 * guardPower;
    parts.leftArm.rotation.z = side * -.95 * guardPower;
    parts.rightArm.rotation.z = side * .95 * guardPower;
    parts.aura.material.opacity = .2 + guardPower * .58;
    parts.aura.scale.setScalar(1 + guardPower * .8);
    shockwave.visible = p > .18 && p < .8;
    shockwave.position.set(source.position.x, 1.18, .05);
    shockwave.material.color.setHex(actor === 'player' ? 0x62ebf7 : 0xe27c4e);
    shockwave.scale.setScalar(.5 + smooth((p - .18) / .55) * 3.2);
    shockwave.material.opacity = Math.max(0, .7 - p * .72);
  }

  if (type === 'recharge') {
    const focus = Math.sin(p * Math.PI);
    source.position.y = focus * .14;
    parts.leftArm.rotation.z = side * -.65 * focus;
    parts.rightArm.rotation.z = side * .65 * focus;
    parts.aura.material.opacity = .16 + focus * .76;
    parts.aura.scale.setScalar(1 + focus * 1.05);
    if (now - actionAnimation.sparkTimer > 90 && p < .82) {
      const sparkPosition = new THREE.Vector3(source.position.x + (Math.random() - .5) * .7, .2, (Math.random() - .5) * .5);
      burstParticles(sparkPosition, actor === 'player' ? 0x54e8f4 : 0xe27b47, 4, .4);
      actionAnimation.sparkTimer = now;
    }
  }

  if (damaging && !actionAnimation.impactTriggered && p >= impactPoint) {
    actionAnimation.impactTriggered = true;
    const impactPosition = new THREE.Vector3(target.userData.baseX - side * .28, 1.28, .2);
    const color = ['sand', 'wind', 'rasengan'].includes(type) ? techniqueColor : actor === 'player' ? currentAffinity().hex : techniqueColor;
    burstParticles(impactPosition, color, type === 'rasengan' ? 34 : 25, type === 'rasengan' ? 1.45 : 1.05);
    shockwave.visible = true;
    shockwave.position.copy(impactPosition);
    shockwave.material.color.setHex(color);
    shockwave.material.opacity = .88;
    shockwave.scale.setScalar(.45);
    cameraShake = state.settings.screenShake ? (type === 'rasengan' || type === 'sand' ? .28 : .18) : 0;
    flashImpact(actor === 'player');
    tone(type === 'rasengan' ? 74 : 92, .2, 'sawtooth');
    triggerAnimationImpact();
  }

  if (damaging && actionAnimation.impactTriggered) {
    const impactAge = THREE.MathUtils.clamp((p - impactPoint) / .2, 0, 1);
    const recoil = Math.sin(impactAge * Math.PI);
    target.position.x = target.userData.baseX + side * recoil * (type === 'rasengan' ? .42 : .26);
    target.position.y = recoil * (type === 'rasengan' ? .16 : .08);
    target.rotation.z = -side * recoil * .13;
    targetParts.head.rotation.z = -side * recoil * .18;
    shockwave.scale.setScalar(.45 + impactAge * (type === 'rasengan' ? 5.2 : 3.5));
    shockwave.material.opacity = Math.max(0, .86 - impactAge);
  }

  if (p >= 1) {
    source.position.set(source.userData.baseX, 0, 0);
    target.position.set(target.userData.baseX, 0, 0);
    source.scale.set(1, 1, 1);
    source.rotation.z = 0;
    source.rotation.y = actor === 'player' ? .08 : -.08;
    target.rotation.z = 0;
    parts.leftArm.rotation.z = 0;
    parts.rightArm.rotation.z = 0;
    parts.leftArm.rotation.x = 0;
    parts.rightArm.rotation.x = 0;
    parts.leftLeg.rotation.z = 0;
    parts.rightLeg.rotation.z = 0;
    parts.leftLeg.rotation.x = 0;
    parts.rightLeg.rotation.x = 0;
    parts.head.rotation.z = 0;
    source.userData.model.rotation.x = 0;
    source.userData.model.rotation.y = 0;
    targetParts.head.rotation.z = 0;
    energyOrb.visible = false;
    energyOrb.material.color.setHex(0x3de5f3);
    energyOrb.material.emissive.setHex(0x16b9d1);
    windRings.forEach((wind) => { wind.visible = false; });
    shadowClones.forEach((clone) => {
      clone.visible = false;
      clone.position.set(clone.userData.baseX, 0, 0);
      clone.rotation.z = 0;
      const cloneParts = clone.userData.parts;
      cloneParts.leftArm.rotation.set(0, 0, 0);
      cloneParts.rightArm.rotation.set(0, 0, 0);
      cloneParts.leftLeg.rotation.set(0, 0, 0);
      cloneParts.rightLeg.rotation.set(0, 0, 0);
      cloneParts.head.rotation.set(0, 0, 0);
    });
    cloneSlashArcs.forEach((arc) => { arc.visible = false; });
    slashArc.visible = false;
    shockwave.visible = false;
    const resolve = actionAnimation.resolve;
    playRiggedAnimation(source, 'idle', .18, true);
    playRiggedAnimation(target, 'idle', .18, true);
    actionAnimation = null;
    resolve();
  }
}

updateUI();
loadSelectedRiggedModels();
animate();

if (import.meta.env.PROD && 'serviceWorker' in navigator) {
  window.addEventListener('load', () => navigator.serviceWorker.register('/sw.js').catch(() => {}));
}
